/** SUNTRONIX ONE — Commercial Quotation Management. Internal-only. No Draft stage: every quotation is SENT when made; delivery is tracked separately (email_sent / whatsapp_sent). */
import { EndClientBar } from "@/components/quotes/EndClientBar";
import { useCallback, useEffect, useRef, useState } from "react";
import ProductEnrichPanel from "@/components/products/ProductEnrichPanel";
import { Link, useSearchParams } from "react-router-dom";
import { useCurrentEstablishmentName } from "@/components/app/EstablishmentSwitcher";
import { OrgName, PersonName } from "@/components/identity/Identity";
import { QuoteToOrderFlow, QuoteTabs } from "@/components/quotes/QuoteToOrderFlow";
import { FulfilmentNotActivated } from "@/components/quotes/fulfilment/FulfilmentDelivery";
import { useAuth } from "@/context/AuthContext";
import { QuoteExperiencePanel, postWithPresend } from "@/components/quotes/QuoteExperiencePanel";
import { TxnHeader } from "@/components/quotes/TxnSections";
import { RecordIntelStrip } from "@/components/app/RecordIntelStrip";
import { AddProductModal } from "@/components/app/AddProductModal";
import { PriceHistoryPanel } from "@/components/quotes/PriceHistoryPanel";
import { GstPriceInput, GstPriceBreakdown } from "@/components/quotes/GstPriceInput";
import { cleanItem, inclToBase, lineCalc } from "@/lib/gstMath";
import SendQuoteWhatsApp from "@/components/commercial/SendQuoteWhatsApp";
import api from "@/lib/api";
import { toast } from "sonner";
import { customerPartNumber } from "@/lib/partNumber";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DeliveryScheduleSelect, PriceValiditySelect } from "@/components/commercial/QuoteTermSelects";
import { Textarea } from "@/components/ui/textarea";
import { BoldableTextarea } from "@/components/commercial/BoldableTextarea";
import { Badge } from "@/components/ui/badge";
import { StatusPill } from "@/components/app/vibe/StatusPill";
import AddCustomerModal from "@/components/app/AddCustomerModal";
import { HsnPicker } from "@/components/app/HsnPicker";
import {
  FileText, Loader2, Plus, RefreshCw, Send, ChevronLeft, Trash2,
  CheckCircle2, AlertTriangle, ThumbsUp, ThumbsDown, RotateCcw, Upload,
  ChevronDown, Package, X, Mail, Sparkles,
  History,
} from "lucide-react";
import { UploadModal as TechUploadModal } from "@/pages/technology/TechLibrary";
import CommercialLifecycleStrip from "@/components/commercial/CommercialLifecycleStrip";
import { AdminChip } from "@/components/app/AdminMode";
import { QuoteFollowUpPanel } from "@/pages/commercial/FollowUp";
import ProcessGuidancePanel from "@/components/commercial/ProcessGuidancePanel";
import { VtigerTable } from "@/components/app/VtigerTable";
import QuoteActionBar from "@/components/commercial/QuoteActions";
import DigitalQuotePanel from "@/components/commercial/DigitalQuotePanel";
import QuoteGstinBanner from "@/components/commercial/QuoteGstinBanner";
import { EntityAddressStrip } from "@/components/address/AddressVerify";
import CopyQuoteNo from "@/components/commercial/CopyQuoteNo";
import STXReferenceNumber from "@/components/common/STXReferenceNumber";
import RecordHistory from "@/components/app/RecordHistory";
import { ScheduleWhatsAppButton } from "@/components/whatsapp/ScheduleWhatsAppButton";
import { SendWhatsAppButton } from "@/components/whatsapp/WaComposer";
import { CopyCoverMessageButton } from "@/components/commercial/QuoteCoverMessage";
import { QuoteRecordActions } from "@/components/commercial/QuoteRecordActions";
import { NextActionBadge } from "@/components/atlas/AtlasContext";
import { QuickActionGroup } from "@/components/quickactions/QuickActionGroup";
import { EnquiryAssistantButton } from "@/components/quotes/EnquiryAssistant";

const fmtINR = (n) => n == null ? "—" :
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR",
    maximumFractionDigits: 0 }).format(n);
// Editor / detail breakdown: exact paise so base + GST reconciles with the total.
const fmtMoney2 = (n, cur) => n == null ? "—" :
  new Intl.NumberFormat("en-IN", { style: "currency", currency: cur || "INR",
    minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const fmtMoney = (n, cur) => n == null ? "—" :
  new Intl.NumberFormat("en-IN", { style: "currency", currency: cur || "INR",
    maximumFractionDigits: 0 }).format(n);

// Customer-facing quote date (DD-MMM-YYYY): immutable issue date once issued,
// else issue timestamp, else created date for drafts.
const quoteDateISO = (q) => q?.quote_date || q?.issued_at || q?.created_at || null;
const MMM = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtQuoteDate = (q) => {
  const s = quoteDateISO(q);
  if (!s) return "—";
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s));
  if (dateOnly) return `${dateOnly[3]}-${MMM[Number(dateOnly[2]) - 1]}-${dateOnly[1]}`;
  const d = new Date(s);
  if (isNaN(d)) return "—";
  return `${String(d.getDate()).padStart(2, "0")}-${MMM[d.getMonth()]}-${d.getFullYear()}`;
};
const isQuoteDraft = (q) => q?.status === "INTERNAL_REVIEW";
const QUOTE_FILTERS = ["all", "SENT", "ACCEPTED", "REJECTED", "EXPIRED"];
const shortDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "");
/* Communication status — separate from Quote Stage (spec §7) */
const CommChips = ({ q, compact = true }) => {
  const items = [q.email_sent && { k: "email", label: `Email ✓ ${shortDate(q.email_sent_at)}`, tone: "bg-sky-50 text-sky-800 border-sky-200" },
    q.whatsapp_sent && { k: "wa", label: `WhatsApp ✓ ${shortDate(q.whatsapp_sent_at)}`, tone: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    !q.email_sent && !q.whatsapp_sent && q.communication_channel && { k: "other", label: `${q.communication_channel === "PORTAL" ? "Portal link" : "Marked sent by hand"} ✓ ${shortDate(q.last_communication_at)}`, title: "Recorded by staff — no provider delivery evidence", tone: "bg-slate-50 text-slate-700 border-slate-200" }].filter(Boolean);
  const ev = q.share_evidence || [];
  if (!items.length && ev.length) return <span className={`rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 font-medium text-amber-800 whitespace-nowrap ${compact ? "text-[9px]" : "text-[10px]"}`} title={`Shared — ${ev.join("; ")}`} data-testid={`quote-comm-evidence-${q.id}`}>Shared · {ev[0]}{ev.length > 1 ? ` +${ev.length - 1}` : ""}</span>;
  if (!items.length) return <span className={`text-slate-400 ${compact ? "text-[10px]" : "text-[11px]"}`} title="No email, WhatsApp, customer view/download, PO or order recorded" data-testid={`quote-comm-none-${q.id}`}>Not yet shared</span>;
  return <span className="inline-flex flex-wrap gap-1" data-testid={`quote-comm-${q.id}`}>{items.map((i) => <span key={i.k} title={i.title || "Provider-accepted send recorded in the email / WhatsApp log"} className={`rounded border px-1.5 py-0.5 ${compact ? "text-[9px]" : "text-[10px]"} font-medium whitespace-nowrap ${i.tone}`}>{i.label}</span>)}</span>;
};


const ASSIGNED_TO_OPTIONS = ["PD", "ops - Divya", "web - Babita", "dm - Vichitra", "Store - Akash", "bo - Karthika", "support - Rajasree"];

const STATUS_TONE = {
  DRAFT: "bg-slate-100 text-slate-700 border-slate-200",
  INTERNAL_REVIEW: "bg-amber-100 text-amber-800 border-amber-200",
  SENT: "bg-blue-100 text-blue-800 border-blue-200",
  ACCEPTED: "bg-emerald-100 text-emerald-800 border-emerald-200",
  REJECTED: "bg-rose-100 text-rose-800 border-rose-200",
  EXPIRED: "bg-slate-100 text-slate-500 border-slate-200",
  CONVERTED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  CANCELLED: "bg-slate-200 text-slate-600 border-slate-300",
  LOST: "bg-rose-50 text-rose-600 border-rose-200",
  SUPERSEDED: "bg-slate-100 text-slate-500 border-slate-300",
};

const quoteStage = (q) => (q.superseded_by ? "SUPERSEDED" : q.status);

// A value that is only digits / + ( ) - spaces (>=7 digits, no letters) is
// almost certainly a phone number mistyped into a name field.
const looksLikePhone = (v) => {
  const s = (v || "").trim();
  if (!s) return false;
  if (/[a-zA-Z]/.test(s)) return false;
  const digits = (s.match(/\d/g) || []).length;
  return digits >= 7 && /^[+()\-\s\d]+$/.test(s);
};

// Treat placeholder HSN/SAC values from Product Master (NA / N/A / - / 0) as empty.
const _cleanHsn = (v) => {
  const s = String(v || "").trim();
  if (!s || ["NA", "N/A", "NIL", "-", "0"].includes(s.toUpperCase())) return null;
  return s;
};




export default function QuotationsList() {
  const issuerName = useCurrentEstablishmentName();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [view, setView] = useState("table");
  const [showNew, setShowNew] = useState(false);
  const [preselectCustomerId, setPreselectCustomerId] = useState(null);
  const [prefill, setPrefill] = useState(null);   // {items, subject} from catalogue Add-to-Quote
  const [selected, setSelected] = useState(null);
  const [missingOpen, setMissingOpen] = useState(null);
  const [editQuote, setEditQuote] = useState(null);
  const [costVisible, setCostVisible] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Global "Quick Create Quote" deep-link — /commercial/crm/quotations?new=1
  //   Optional: &customer_id=<id> pre-selects the customer.
  //   Optional: &status=<STATUS> sets the initial status filter.
  //   Optional: &open=<qid> opens the quotation drawer for that id.
  useEffect(() => {
    let touched = false;
    if (searchParams.get("new") === "1") {
      setShowNew(true);
      const cid = searchParams.get("customer_id");
      if (cid) setPreselectCustomerId(cid);
      if (searchParams.get("prefill") === "1") {
        try {
          const raw = sessionStorage.getItem("st:quote-prefill");
          if (raw) setPrefill(JSON.parse(raw));
        } catch { /* ignore */ }
        sessionStorage.removeItem("st:quote-prefill");
        searchParams.delete("prefill");
      }
      searchParams.delete("new");
      searchParams.delete("customer_id");
      touched = true;
    }
    const st = searchParams.get("status");
    if (st) {
      // MyWork uses REVISION as a friendly alias — map to the real value.
      const alias = { REVISION: "INTERNAL_REVIEW",
                                        AWAITING_CUSTOMER: "SENT" };
      setStatusFilter(alias[st.toUpperCase()] || st);
      searchParams.delete("status");
      touched = true;
    }
    const openId = searchParams.get("open");
    if (openId && rows.length) {
      const hit = rows.find((r) => r.id === openId || r.code === openId);
      if (hit) {
        setSelected(hit);
      } else {
        api.get(`/vtiger/quotations/${encodeURIComponent(openId)}`).then((r) => (r.data?.id ? setSelected(r.data) : setMissingOpen(openId))).catch(() => setMissingOpen(openId));
      }
      searchParams.delete("open");
      touched = true;
    }
    if (touched) setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, rows]);

  // Optional: /commercial/crm/quotations?customer_id=<id> filters the
  // list without opening the create drawer.
  const listCustomerId = searchParams.get("customer_id") ||
                                          new URLSearchParams(window.location.search).get("customer_id");

  const [serverQ, setServerQ] = useState("");
  const [liveQ, setLiveQ] = useState("");          // debounced / Enter-committed search term
  const [yearFilter, setYearFilter] = useState("");
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [matchCustomers, setMatchCustomers] = useState([]);
  const PAGE = 100;
  const SEARCH_PAGE = 500;
  const searching = liveQ.trim().length > 0;
  // Live search: debounce ~300 ms; exact quote no. / SKU-looking terms (no spaces, ≥4 chars) search at once; fuzzy needs ≥2 chars.
  useEffect(() => {
    const t = serverQ.trim();
    if (!t) { setLiveQ(""); return undefined; }
    if (t.length < 2) return undefined;
    const immediate = !t.includes(" ") && t.length >= 4 && /^[A-Za-z0-9\-\/_.]+$/.test(t) && /\d/.test(t);
    const h = setTimeout(() => setLiveQ(t), immediate ? 0 : 300);
    return () => clearTimeout(h);
  }, [serverQ]);
  const commitSearch = () => setLiveQ(serverQ.trim());
  const baseParams = useCallback(() => ({
    ...(statusFilter !== "all" ? { status: statusFilter } : {}),
    ...(yearFilter ? { year: yearFilter } : {}),
    ...(listCustomerId ? { customer_id: listCustomerId } : {}),
  }), [statusFilter, yearFilter, listCustomerId]);
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/vtiger/quotations",
        { params: searching ? { q: liveQ, limit: SEARCH_PAGE, skip: 0, ...baseParams() } : { limit: PAGE, skip: 0, ...baseParams() } });
      setRows(data.items || []);
      setTotal(data.total ?? (data.items || []).length);
      setHasMore(!!data.has_more);
      setMatchCustomers(data.customers || []);
      setCostVisible(!!data.cost_visible);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load quotations");
    } finally { setBusy(false); }
  }, [liveQ, searching, baseParams]);

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const { data } = await api.get("/vtiger/quotations", { params: { ...(searching ? { q: liveQ, limit: SEARCH_PAGE } : { limit: PAGE }), skip: rows.length, ...baseParams() } });
      setRows((prev) => {
        const seen = new Set(prev.map((r) => r.id));
        return [...prev, ...(data.items || []).filter((r) => !seen.has(r.id))];
      });
      setTotal(data.total ?? total);
      setHasMore(!!data.has_more);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load more");
    } finally { setLoadingMore(false); }
  }, [rows.length, total, searching, liveQ, baseParams]);
  useEffect(() => { load(); }, [load]);

  const clearSearch = () => { setServerQ(""); setLiveQ(""); };
  const clearFilters = () => { setStatusFilter("all"); setYearFilter(""); if (listCustomerId) { searchParams.delete("customer_id"); setSearchParams(searchParams, { replace: true }); } };
  const viewAllForCustomer = (accountId) => { searchParams.set("customer_id", accountId); setSearchParams(searchParams, { replace: true }); clearSearch(); };
  const filtered = rows;   // status / year / customer filters are applied server-side across ALL quotations
  const YEARS = Array.from({ length: new Date().getFullYear() - 2017 }, (_, i) => String(new Date().getFullYear() - i));

  return (
    <div className="space-y-2.5 max-w-[1600px]" data-testid="crm-quotations-page">
      {missingOpen && (
        <div role="alert" className="flex items-start justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900" data-testid="quote-unavailable">
          <span><b>Quotation unavailable.</b> “{missingOpen}” does not exist, was removed, or you do not have access to it. Use the list below to find the right quotation.</span>
          <button type="button" className="text-[11px] font-semibold underline" onClick={() => setMissingOpen(null)} data-testid="quote-unavailable-dismiss">Dismiss</button>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <Link to="/commercial/crm" className="text-[11px] text-emerald-700 hover:underline flex items-center gap-1"
            data-testid="crm-back">
            <ChevronLeft className="h-3 w-3" /> Commercial Workspace
          </Link>
          <h1 className="text-xl font-heading font-semibold text-slate-900 mt-0.5 flex items-center gap-2">
            <FileText className="h-5 w-5 text-emerald-700" />
            Quotations
            <AdminChip area="quotation" label="Configure Quote Form" testid="admin-chip-quotation" />
            <AdminChip area="pdf_template" label="Edit PDF template" testid="admin-chip-quotation-pdf" />
            <AdminChip area="numbering" label="Edit numbering" testid="admin-chip-quotation-numbering" />
          </h1>
          <p className="text-[11px] text-slate-500" data-testid="crm-quotations-subtitle">
            Create, manage and track {issuerName ? <b className="font-bold uppercase" data-testid="crm-quotations-issuer">{issuerName}</b> : "company"} quotations.
          </p>
        </div>
        <div className="flex min-w-0 flex-wrap justify-end gap-2" data-testid="crm-quote-toolbar">
          <ScheduleWhatsAppButton kind="quotation" />
          <Button variant="outline" onClick={load} disabled={busy}
            data-testid="crm-quote-refresh">
            <RefreshCw className={"h-4 w-4 mr-1.5 " + (busy ? "animate-spin" : "")} />
            Refresh
          </Button>
          <EnquiryAssistantButton testid="ea-open-list" />
          <Button variant="create" onClick={() => setShowNew(true)}
            className=""
            data-testid="crm-quote-new">
            <Plus className="h-4 w-4 mr-1.5" /> New quotation
          </Button>
        </div>
      </div>
      <QuickActionGroup module="quotations" />


      <div className="bg-white border border-slate-200 rounded-md p-2 flex gap-2 items-center flex-wrap">
        {QUOTE_FILTERS.map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)}
            data-testid={`quote-filter-${s}`}
            className={"h-8 px-3 rounded text-[12px] font-medium " +
              (statusFilter === s
                ? "bg-slate-900 text-white"
                : "text-slate-600 hover:bg-slate-100")}>
            {s === "all" ? "All" : s}
          </button>
        ))}
        <select value={yearFilter} onChange={(e) => setYearFilter(e.target.value)} data-testid="quotes-year-filter"
          className="h-8 px-2 text-[12px] border border-slate-200 rounded text-slate-700">
          <option value="">All years</option>
          {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        {(statusFilter !== "all" || yearFilter || listCustomerId) && (
          <button onClick={clearFilters} className="h-8 px-2 text-[11px] text-rose-700 hover:underline" data-testid="quotes-clear-filters">Clear filters</button>
        )}
        <span className="ml-auto inline-flex items-center gap-2 flex-wrap justify-end">
          <span className="relative inline-flex items-center w-full sm:w-96 max-w-full">
            <input value={serverQ} onChange={(e) => setServerQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && commitSearch()}
                placeholder="Search quote no., customer, contact, PO, product or part no..."
                data-testid="quotes-server-search"
                className="h-8 w-full px-2 pr-7 text-[11px] border border-slate-200 rounded" />
            {serverQ && <button onClick={clearSearch} aria-label="Clear search" data-testid="quotes-clear-search"
                className="absolute right-1.5 text-slate-400 hover:text-slate-700 text-[13px] leading-none">✕</button>}
          </span>
          <span className="inline-flex rounded border border-slate-200 overflow-hidden">
          {["table", "cards"].map((v) => (
            <button key={v} onClick={() => setView(v)} data-testid={`quotes-view-${v}`}
                className={"h-8 px-3 text-[11px] font-medium " +
                  (view === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100")}>
              {v === "table" ? "Table" : "Cards"}</button>))}
          </span>
        </span>
        <span className="text-[11px] text-slate-500" data-testid="quotes-result-count">
          {searching ? <>{total.toLocaleString("en-IN")} matching quotation{total === 1 ? "" : "s"}{rows.length < total ? ` · showing first ${rows.length}` : ""}</> : <>{total.toLocaleString("en-IN")} quotation{total === 1 ? "" : "s"}{rows.length < total ? ` · ${rows.length} loaded` : ""}</>} · {costVisible ? "cost visible" : "cost hidden"}
        </span>
      </div>
      {searching && matchCustomers.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="quotes-customer-history">
          {matchCustomers.slice(0, 3).map((c) => (
            <div key={c.account_id} className="inline-flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-[11.5px] text-emerald-900">
              <span className="font-semibold truncate max-w-[280px]" title={c.customer_name}>{c.customer_name}</span>
              <span className="text-emerald-700">{c.total_quotations.toLocaleString("en-IN")} quotation{c.total_quotations === 1 ? "" : "s"}</span>
              <button onClick={() => viewAllForCustomer(c.account_id)} className="underline font-medium" data-testid={`quotes-view-all-${c.account_id}`}>View all</button>
            </div>
          ))}
        </div>
      )}
      {listCustomerId && !searching && (
        <div className="text-[11.5px] text-slate-600 inline-flex items-center gap-2" data-testid="quotes-customer-scope">
          Showing all quotations for the selected customer ({total.toLocaleString("en-IN")}). <button onClick={clearFilters} className="underline text-rose-700">Clear</button>
        </div>
      )}

      {view === "table" ? (
        <VtigerTable testid="quotes-table" rows={filtered} pageSize={20}
          viewKey="quotations"
          filterNote="Column filters apply to loaded rows — use the server search above for all 10k+ quotes"
          summary={
            <span className="inline-flex items-baseline gap-1.5" data-testid="quotes-totals-bar">
              <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Total</span>
              <span className="text-[13px] font-bold text-slate-900" data-testid="quotes-total-amount">
                {fmtMoney(filtered.reduce((s, q) => s + (Number(q.grand_total_inr) || 0), 0), "INR")}
              </span>
              <span className="text-slate-400">·</span>
              <span className="text-[11px] text-slate-600"><span className="font-semibold text-slate-900" data-testid="quotes-total-count">{filtered.length}</span> quotations{statusFilter !== "all" && <span className="text-slate-400"> · {statusFilter}</span>}</span>
            </span>
          }
          onRowClick={(q) => setSelected(q)}
          columns={[
            ...(searching ? [{ key: "_match", label: "Matched", width: 150, sortable: false,
              render: (q) => <span className="text-[10.5px] text-emerald-800 bg-emerald-50 border border-emerald-100 rounded px-1.5 py-0.5 truncate block" title={q._match} data-testid={`quote-match-${q.id}`}>{q._match || "—"}</span> }] : []),
            { key: "customer_name", label: "Organization Name", width: 220,
              render: (q) => <span className="text-sky-700 truncate block w-full" title={q.customer_name || q.customer_account_name}>{(q.customer_name || q.customer_account_name) ? <OrgName name={q.customer_name || q.customer_account_name} /> : "—"}</span>,
              value: (q) => q.customer_name || q.customer_account_name },
            { key: "code", label: "Quote No.", mono: true, width: 240, align: "left",
              cls: "whitespace-nowrap",
              render: (q) => (
                <span className="flex w-full items-center justify-start gap-1 whitespace-nowrap" data-testid={`quote-code-cell-${q.id}`}>
                  <STXReferenceNumber value={q.code} copy asChildOfRow size={13} className="text-sky-700" />
                </span>) },
            { key: "quote_date", label: "Date", width: 110, align: "center",
              cls: "whitespace-nowrap",
              value: (q) => quoteDateISO(q) || "",
              render: (q) => (
                <span className="whitespace-nowrap text-slate-700" data-testid={`quote-date-${q.id}`} title="Quote date">
                  {fmtQuoteDate(q)}
                </span>
              ) },
            { key: "subject", label: "Subject",
              value: (q) => q.subject || "",
              render: (q) => <span className="text-slate-700 truncate block w-full" title={q.subject}>{q.subject || "—"}</span> },
            { key: "grand_total_inr", label: "Amount", align: "center", num: true, width: 130,
              cls: "whitespace-nowrap",
              value: (q) => q.grand_total_inr,
              render: (q) => <span className="font-semibold text-slate-900">{fmtMoney(q.grand_total_inr, q.currency)}</span> },
            { key: "status", label: "Quote Stage", width: 120, align: "center",
              render: (q) => <StatusPill status={quoteStage(q)} /> },
            { key: "created_by", label: "Assigned To", width: 120, align: "center",
              value: (q) => q.assigned_to_label || q.assigned_to || q.created_by,
              render: (q) => <span className="block truncate" title={q.assigned_to_label || q.assigned_to || q.created_by}>{q.assigned_to_label || (q.assigned_to || q.created_by || "—").split("@")[0]}</span> },
            { key: "communication_channel", label: "Shared", width: 120, align: "center", sortable: false,
              value: (q) => [q.email_sent && "EMAIL", q.whatsapp_sent && "WHATSAPP"].filter(Boolean).join(" ") || (q.communication_channel || ""),
              render: (q) => <CommChips q={q} /> },
            { key: "_actions", label: "Actions", width: 96, sortable: false, align: "center",
              render: (q) => (
                <span className="inline-flex gap-1" onClick={(e) => e.stopPropagation()}>
                  <button title={isQuoteDraft(q) ? "Edit (internal review)" : "Open (revise inside)"}
                    data-testid={`quote-row-edit-${q.id}`}
                    onClick={() => isQuoteDraft(q) ? setEditQuote(q) : setSelected(q)}
                    className="h-6 w-6 inline-flex items-center justify-center rounded border border-slate-200 text-slate-500 hover:text-slate-900 hover:border-slate-400">
                    ✎
                  </button>
                  <a title="Preview PDF" data-testid={`quote-row-pdf-${q.id}`}
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/vtiger/quotations/${q.id}/pdf/preview`}
                    target="_blank" rel="noreferrer"
                    className="h-6 w-6 inline-flex items-center justify-center rounded border border-slate-200 text-slate-500 hover:text-slate-900 hover:border-slate-400">
                    ⤓
                  </a>
                </span>
              ) },
          ]} />
      ) : (
      <div className="bg-white border border-slate-200 rounded-md divide-y">
        <div className="flex items-center justify-end gap-2 px-3 py-1.5 border-b border-slate-100 text-[11px] text-slate-500" data-testid="quotes-cards-summary">
          <span className="inline-flex items-baseline gap-1.5">
            <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Total</span>
            <span className="text-[13px] font-bold text-slate-900">{fmtMoney(filtered.reduce((s, q) => s + (Number(q.grand_total_inr) || 0), 0), "INR")}</span>
            <span className="text-slate-400">·</span>
            <span className="text-[11px] text-slate-600"><span className="font-semibold text-slate-900">{filtered.length}</span> quotations</span>
          </span>
        </div>
        {filtered.length === 0 && (
          <div className="p-10 text-center text-[13px] text-slate-500">
            No quotations yet. Click &ldquo;New quotation&rdquo; to create one.
          </div>
        )}
        {filtered.map((q) => (
          <button key={q.id} onClick={() => setSelected(q)}
            data-testid={`quote-row-${q.id}`}
            className="w-full text-left p-4 hover:bg-slate-50 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <STXReferenceNumber value={q.code} copy asChildOfRow size={13} />
                <Badge className={"text-[10px] border " + (STATUS_TONE[q.status] || "")}>{q.status}</Badge>
                <CommChips q={q} />
              </div>
              <div className="text-[12px] text-slate-700 mt-1 truncate">{q.subject}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                {q.customer_contact_name ? <PersonName name={q.customer_contact_name} /> : (q.customer_email || "—")} ·
                {" "}{fmtQuoteDate(q)} · {q.items?.length || 0} line(s)
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.1em] text-slate-500">Grand total</div>
              <div className="text-[15px] font-heading font-semibold text-slate-900">
                {fmtMoney(q.grand_total_inr, q.currency)}
              </div>
              {costVisible && q.gross_margin_inr != null && (
                <div className="text-[10px] text-emerald-700 mt-0.5">
                  GM {fmtMoney(q.gross_margin_inr, q.currency)} ({q.margin_pct ?? 0}%)
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
      )}

      {!serverQ.trim() && (
        <div className="flex flex-col items-center gap-1.5 py-2" data-testid="quotes-loadmore-wrap">
          <div className="text-[11px] text-slate-500" data-testid="quotes-loaded-count">
            Showing {rows.length.toLocaleString()} of {total.toLocaleString()} quotations
          </div>
          {hasMore && (
            <Button variant="outline" onClick={loadMore} disabled={loadingMore}
              data-testid="quotes-load-more">
              {loadingMore
                ? <span className="inline-flex items-center"><RefreshCw className="h-4 w-4 mr-1.5 animate-spin" />Loading…</span>
                : <span className="inline-flex items-center"><ChevronDown className="h-4 w-4 mr-1.5" />Load older quotations</span>}
            </Button>
          )}
        </div>
      )}

      {showNew && (
        <NewQuoteDrawer
          preselectCustomerId={preselectCustomerId}
          prefill={prefill}
          onClose={() => { setShowNew(false); setPreselectCustomerId(null); setPrefill(null); }}
          onCreated={() => { setShowNew(false); setPreselectCustomerId(null); setPrefill(null); load(); }}
        />
      )}

      {editQuote && (
        <NewQuoteDrawer
          editQuote={editQuote}
          onClose={() => setEditQuote(null)}
          onCreated={() => { setEditQuote(null); load(); }}
        />
      )}

      {selected && (
        <QuoteDrawer
          key={selected.id}
          q={selected}
          costVisible={costVisible}
          onClose={() => setSelected(null)}
          onSaved={async () => {
            const id = selected.id;
            load();
            try {
              const { data } = await api.get(`/vtiger/quotations/${encodeURIComponent(id)}`);
              setSelected((current) => current?.id === id ? data : current);
            } catch { toast.error("Saved, but the quotation could not be refreshed. Keep this workspace open and retry."); }
          }}
          onFitChanged={(id, mode) => {
            setRows((prev) => prev.map((r) => r.id === id ? { ...r, pdf_fit: mode ? { description_mode: mode } : undefined, pdf_fit_hint: undefined } : r));
            setSelected((s) => s && s.id === id ? { ...s, pdf_fit: mode ? { description_mode: mode } : undefined, pdf_fit_hint: undefined } : s);
          }}
          onFitChanged={(id, mode) => {
            setRows((prev) => prev.map((r) => r.id === id ? { ...r, pdf_fit: mode ? { description_mode: mode } : undefined, pdf_fit_hint: undefined } : r));
            setSelected((s) => s && s.id === id ? { ...s, pdf_fit: mode ? { description_mode: mode } : undefined, pdf_fit_hint: undefined } : s);
          }}
          onEdit={(quote) => { setSelected(null); setEditQuote(quote); }}
        />
      )}
    </div>
  );
}


function NewQuoteDrawer({ onClose, onCreated, preselectCustomerId, editQuote, prefill }) {
  const [priceHist, setPriceHist] = useState(null);
  const [inlineProduct, setInlineProduct] = useState(false);
  const isEdit = !!editQuote;
  const prefillItems = (!isEdit && prefill?.items?.length)
    ? prefill.items.map((it) => ({
        line_type: it.line_type || "PRODUCT",
        product_id: it.product_id || null, product_code: it.product_code || null,
        part_number: it.part_number || null, product_name: it.product_name || "",
        description: it.description || "", sku: it.sku || null,
        quantity: it.quantity ?? 1, unit_price_inr: it.unit_price_inr ?? 0,
        vendor_unit_cost_inr: it.vendor_unit_cost_inr ?? 0,
        tax_pct: it.tax_pct ?? 18, discount_pct: it.discount_pct ?? 0,
        notes: it.notes || null,
      }))
    : null;
  const initItems = () => isEdit && editQuote.items?.length
    ? editQuote.items.map((it) => ({
        line_type: it.line_type || "PRODUCT", service_id: it.service_id || null,
        service_code: it.service_code || null, product_id: it.product_id || null,
        product_code: it.product_code || null, part_number: it.part_number || null,
        product_name: it.product_name || "", description: it.description || "",
        sku: it.sku || null, hsn_sac: _cleanHsn(it.hsn_sac),
        quantity: it.quantity ?? 1, unit_of_measure: it.unit_of_measure || null,
        unit_price_inr: it.unit_price_inr ?? 0,
        vendor_unit_cost_inr: it.vendor_unit_cost_inr ?? 0,
        tax_pct: it.tax_pct ?? 18, discount_pct: it.discount_pct ?? 0,
      }))
    : prefillItems || [{ product_name: "", quantity: 1, unit_price_inr: 0,
         vendor_unit_cost_inr: 0, tax_pct: 18, discount_pct: 0 }];
  const [subject, setSubject] = useState(isEdit ? editQuote.subject || "" : (prefill?.subject || ""));
  const [assignedToLabel, setAssignedToLabel] = useState(isEdit ? (editQuote.assigned_to_label || "") : "");
  const [bankDisplay, setBankDisplay] = useState(isEdit ? (editQuote.bank_display || "AUTO") : "AUTO");
  const [customer, setCustomer] = useState(isEdit && editQuote.customer_account_id
    ? { id: editQuote.customer_account_id, account_name: editQuote.customer_name,
        customer_code: editQuote.customer_code, gstin: editQuote.customer_gstin,
        email: editQuote.customer_email, contact_name: editQuote.customer_contact_name }
    : null);        // selected crm_accounts row
  const [contact, setContact] = useState(null);          // selected crm_contacts row
  const [customerName, setCustomerName] = useState(isEdit ? editQuote.customer_contact_name || "" : ""); // contact name override
  const _initTitle = isEdit ? (editQuote.customer_contact_title || "None") : "None";
  const _std = ["None", "Mr.", "Ms.", "Mrs.", "Dr.", "Prof."];
  const [contactTitle, setContactTitle] = useState(_std.includes(_initTitle) ? _initTitle : "Other");
  const [otherTitle, setOtherTitle] = useState(_std.includes(_initTitle) ? "" : _initTitle);
  const [displayPrefix, setDisplayPrefix] = useState(isEdit ? (editQuote.customer_display_prefix || "M/s.") : "M/s.");
  const [designation, setDesignation] = useState(isEdit ? (editQuote.customer_contact_designation || "") : "");
  const resolvedTitle = contactTitle === "Other" ? (otherTitle.trim() || null) : (contactTitle === "None" ? null : contactTitle);
  const [customerEmail, setCustomerEmail] = useState(isEdit ? editQuote.customer_email || "" : "");
  const [customerPhone, setCustomerPhone] = useState(isEdit ? editQuote.customer_contact_phone || "" : "");
  const [billingAddress, setBillingAddress] = useState(isEdit ? editQuote.customer_billing_address || "" : "");
  const [shippingAddress, setShippingAddress] = useState(isEdit ? editQuote.customer_shipping_address || "" : "");
  const [overrideAddress, setOverrideAddress] = useState(false);
  const [items, setItems] = useState(initItems);
  const [lineMeta, setLineMeta] = useState({}); // product_id -> {url, colour}
  const [currency, setCurrency] = useState(isEdit ? editQuote.currency || "INR" : "INR");
  const [paymentTerms, setPaymentTerms] = useState(isEdit ? editQuote.payment_terms || "" : "");
  const [validUntil, setValidUntil] = useState(isEdit ? editQuote.valid_until || "" : "");
  const [deliverySchedule, setDeliverySchedule] = useState(isEdit ? editQuote.delivery_schedule || "" : "");
  const [priceValidity, setPriceValidity] = useState(isEdit ? editQuote.price_validity || "" : "");
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addInitialName, setAddInitialName] = useState("");
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [editContact, setEditContact] = useState(null);  // when set, modal edits this contact
  const [intel, setIntel] = useState(null);   // commercial intelligence for selected customer
  const [draftStatus, setDraftStatus] = useState("idle"); // idle|saving|saved|error
  const [recoverable, setRecoverable] = useState(null);   // server draft offered for recovery
  const [draftReady, setDraftReady] = useState(false);    // gate autosave until recovery check done

  // ---- Composer image/colour preview: fetch thumbnails for products on the lines
  useEffect(() => {
    const pids = Array.from(new Set(
      items.filter((it) => it.product_id && (it.line_type || "PRODUCT") !== "SERVICE")
           .map((it) => it.product_id)));
    const need = pids.filter((id) => !(id in lineMeta));
    if (need.length === 0) return;
    api.post("/pc/image-map", { product_ids: need })
      .then(({ data }) => {
        const imgs = data.images || {}; const meta = data.meta || {};
        setLineMeta((prev) => {
          const next = { ...prev };
          need.forEach((id) => {
            next[id] = { url: imgs[id] || null, colour: (meta[id] || {}).colour || null };
          });
          return next;
        });
      }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // ---- Draft recovery: offer the last unsubmitted draft on open (create mode only)
  useEffect(() => {
    if (isEdit) { setDraftReady(true); return; }
    if (prefillItems) { setDraftReady(true); return; }  // catalogue prefill wins over draft recovery
    (async () => {
      try {
        const { data } = await api.get("/inline/drafts/quote");
        const p = data.draft?.payload;
        if (p && (p.subject || (p.items || []).some((it) => it.product_name))) {
          setRecoverable(data.draft);
        }
      } catch { /* silent */ }
      setDraftReady(true);
    })();
  }, [isEdit, prefillItems]);

  const recoverDraft = () => {
    const p = recoverable.payload;
    setSubject(p.subject || "");
    if (p.customer) setCustomer(p.customer);
    if (p.contact) setContact(p.contact);
    setCustomerName(p.customerName || "");
    setCustomerEmail(p.customerEmail || "");
    setCustomerPhone(p.customerPhone || "");
    setBillingAddress(p.billingAddress || "");
    setShippingAddress(p.shippingAddress || "");
    setOverrideAddress(!!p.overrideAddress);
    if (p.items?.length) setItems(p.items);
    if (p.currency) setCurrency(p.currency);
    if (p.paymentTerms) setPaymentTerms(p.paymentTerms);
    if (p.validUntil) setValidUntil(p.validUntil);
    setRecoverable(null);
    toast.success("Draft recovered");
  };

  const discardDraft = async () => {
    setRecoverable(null);
    try { await api.delete("/inline/drafts/quote"); } catch { /* silent */ }
  };

  // ---- Autosave (debounced) — never interrupts typing
  const draftPayload = { subject, customer, contact, customerName, customerEmail, customerPhone,
    billingAddress, shippingAddress, overrideAddress, items,
    currency, paymentTerms, validUntil };
  const draftJson = JSON.stringify(draftPayload);
  const lastSavedRef = useRef(null);
  useEffect(() => {
    if (isEdit && lastSavedRef.current === null) lastSavedRef.current = draftJson;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildPatch = () => ({
    revision_reason: editQuote?._revision_reason || null,
    revision_note: editQuote?._revision_note || null,
    subject,
    assigned_to_label: assignedToLabel || null,
    bank_display: bankDisplay || "AUTO",
    customer_account_id: customer?.id || null,
    customer_contact_id: contact?.id || null,
    customer_contact_name: customerName || contact?.full_name || null,
    customer_contact_title: resolvedTitle,
    customer_display_prefix: displayPrefix || null,
    customer_contact_designation: designation || null,
    customer_contact_phone: customerPhone || contact?.mobile || contact?.phone || null,
    customer_email: customerEmail || contact?.email || null,
    customer_billing_address: billingAddress || null,
    customer_shipping_address: shippingAddress || null,
    currency,
    payment_terms: paymentTerms || null,
    valid_until: validUntil || null,
    delivery_schedule: deliverySchedule.trim() || null,
    price_validity: priceValidity.trim() || null,
    items: items.map(cleanItem),
  });

  useEffect(() => {
    if (!draftReady || recoverable || isEdit) return;
    const dirty = subject || items.some((it) => it.product_name);
    if (!dirty || draftJson === lastSavedRef.current) return;
    const t = setTimeout(async () => {
      setDraftStatus("saving");
      try {
        if (isEdit) {
          await api.patch(`/vtiger/quotations/${editQuote.id}`, buildPatch());
        } else {
          await api.post("/inline/drafts", { kind: "quote", payload: draftPayload });
        }
        lastSavedRef.current = draftJson;
        setDraftStatus("saved");
      } catch { setDraftStatus("error"); }
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftJson, draftReady, recoverable]);

  // Unsaved-change guard — browser navigation + drawer close.
  const hasUnsaved = () => draftStatus === "saving"
    || (isEdit && lastSavedRef.current !== null && draftJson !== lastSavedRef.current);
  useEffect(() => {
    if (!isEdit) return;
    const h = (e) => { if (hasUnsaved()) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftJson, draftStatus]);
  const guardedClose = () => {
    if (hasUnsaved() && !window.confirm("UNSAVED CHANGES — a pending edit has not saved yet. Leave anyway?")) return;
    onClose();
  };
  // Discard = close WITHOUT persisting anything (drops the recoverable local draft too). Nothing is created.
  const discardAndClose = async () => {
    const hasContent = !!(subject || customer || items.some((it) => (it.product_name || "").trim()));
    if (hasContent && !window.confirm(isEdit ? "Discard your changes? The quotation stays as it was." : "Discard this quotation? Nothing will be saved.")) return;
    if (!isEdit) await discardDraft();
    onClose();
  };
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); guardedClose(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUnsaved]);

  // Deep-link preselect: /commercial/crm/quotations?new=1&customer_id=<id>
  useEffect(() => {
    if (!preselectCustomerId) return;
    (async () => {
      try {
        const { data } = await api.get(`/vtiger/crm-customers/${preselectCustomerId}`);
        if (data) pickCustomer(data);
      } catch { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectCustomerId]);

  // Commercial intelligence — last quote / accepted / product hint
  useEffect(() => {
    if (!customer?.id) { setIntel(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/sales-desk/customer/${customer.id}/intel`);
        setIntel(data);
      } catch { setIntel(null); }
    })();
  }, [customer?.id]);

  const pickCustomer = async (c) => {
    setCustomer(c);
    // Hydrate with the primary contact when the list row didn't include one.
    let full = c;
    if (c.primary_contact === undefined && c.id) {
      try {
        const { data } = await api.get(`/vtiger/crm-customers/${c.id}`);
        if (data) { full = data; setCustomer(data); }
      } catch { /* keep list row */ }
    }
    // Prefer the purpose-aware suggestion (Quote → Procurement, else primary).
    let pc = full.primary_contact || null;
    let suggestReason = null, matchedRole = null;
    if (c.id) {
      try {
        const { data } = await api.get("/vtiger/crm-contacts/suggest",
          { params: { customer_id: c.id, purpose: "QUOTE" } });
        if (data?.contact) { pc = data.contact; suggestReason = data.reason; matchedRole = data.matched_role; }
      } catch { /* fall back to primary_contact */ }
    }
    setContact(pc ? { id: pc.id, full_name: pc.full_name || pc.name, email: pc.email,
      mobile: pc.mobile, phone: pc.phone, designation: pc.designation,
      is_primary: pc.is_primary, synthesised: pc.synthesised, ambiguous: pc.ambiguous,
      suggested_reason: suggestReason, matched_role: matchedRole } : null);
    setCustomerName(pc?.full_name || pc?.name || full.contact_name || "");
    setCustomerEmail(pc?.email || full.email || "");
    setCustomerPhone(pc?.mobile || pc?.phone || full.phone || full.mobile || "");
    setDesignation(pc?.designation || "");
    setBillingAddress(full.billing_address || full.billing_street || "");
    setShippingAddress(full.shipping_address || full.shipping_street || full.billing_address || "");
    setOverrideAddress(false);
    const days = Number(full.default_validity_days) || 0;
    if (full.default_currency) setCurrency(full.default_currency);
    if (full.default_payment_terms) setPaymentTerms(full.default_payment_terms);
    if (days > 0) setValidUntil(new Date(Date.now() + days * 86400000).toISOString().slice(0, 10));
    setDefaultsApplied(!!(full.default_currency || full.default_payment_terms || days > 0));
    if (full.display_prefix) setDisplayPrefix(full.display_prefix);
    const _t = pc?.title || pc?.salutation || full.contact_title;
    if (_t && _t !== "None") {
      if (_std.includes(_t)) { setContactTitle(_t); }
      else { setContactTitle("Other"); setOtherTitle(_t); }
    }
  };

  const pickContact = (ct) => {
    setContact(ct);
    setCustomerName(ct.full_name || ct.name || "");
    setCustomerEmail(ct.email || "");
    setCustomerPhone(ct.mobile || ct.phone || "");
    setDesignation(ct.designation || "");
    const _t = ct.title || ct.salutation;
    if (_t && _t !== "None") {
      if (_std.includes(_t)) { setContactTitle(_t); setOtherTitle(""); }
      else { setContactTitle("Other"); setOtherTitle(_t); }
    }
  };

  const addLine = () => setItems((s) => [...s, {
    product_name: "", quantity: 1, unit_price_inr: 0,
    vendor_unit_cost_inr: 0, tax_pct: 18, discount_pct: 0,
  }]);
  const rmLine = (i) => setItems((s) => s.filter((_, ix) => ix !== i));
  const patchLine = (i, patch) => setItems((s) => s.map((it, ix) => (ix === i ? { ...it, ...patch } : it)));
  const upd = (i, k, v) => setItems((s) => s.map((it, ix) => {
    if (ix !== i) return it;
    // Including-GST entry keeps the typed inclusive price when the GST rate changes (base re-derived, never double GST).
    if (k === "tax_pct" && it._price_mode === "INCL" && it._incl_draft != null) return { ...it, tax_pct: v, unit_price_inr: inclToBase(it._incl_draft, v === "" ? 18 : v) };
    return { ...it, [k]: v };
  }));
  const applyHistoryPrice = (i, row) => {
    const prev = items[i];
    patchLine(i, { unit_price_inr: Number(row.unit_price) || 0, discount_pct: Number(row.discount_pct) || 0, tax_pct: row.tax_pct ?? prev.tax_pct, _price_mode: "EXCL", _incl_draft: undefined });
    setPriceHist(null);
    toast.success(`Line ${i + 1}: price from ${row.code} applied`, { action: { label: "Undo", onClick: () => patchLine(i, { ...prev }) } });
  };

  const totals = items.reduce((acc, it) => {
    const c = lineCalc(it);
    acc.subtotal += c.sub; acc.tax += c.tax; acc.total += c.total;
    return acc;
  }, { subtotal: 0, tax: 0, total: 0 });

  const submit = async () => {
    if (!subject || !customer || items.length === 0) {
      toast.error("Subject, customer and at least one line are required");
      return;
    }
    setBusy(true);
    try {
      if (isEdit) {
        const { data } = await api.patch(`/vtiger/quotations/${editQuote.id}`, buildPatch());
        lastSavedRef.current = draftJson;
        if (data?.revised_from) toast.success(`Revision ${data.code} created — ${data.revised_from.code} is now superseded`);
        else toast.success(`${editQuote.code} updated`);
        onCreated();
        return;
      }
      const payload = {
        subject,
        assigned_to_label: assignedToLabel || null,
        bank_display: bankDisplay || "AUTO",
        customer_account_id:        customer.id,
        customer_contact_id:         contact?.id || null,
        customer_contact_name:  customerName || contact?.full_name || null,
        customer_contact_title: resolvedTitle,
        customer_display_prefix: displayPrefix || null,
        customer_contact_designation: designation || null,
        customer_contact_phone: customerPhone || contact?.mobile || contact?.phone || null,
        customer_email:               customerEmail || contact?.email || null,
        customer_billing_address: (overrideAddress ? billingAddress : null) || billingAddress || null,
        customer_shipping_address: (overrideAddress ? shippingAddress : null) || shippingAddress || null,
        currency,
        payment_terms: paymentTerms || null,
        valid_until: validUntil || null,
        delivery_schedule: deliverySchedule.trim() || null,
        price_validity: priceValidity.trim() || null,
        items: items.map(cleanItem),
      };
      const { data } = await api.post("/vtiger/quotations", payload);
      toast.success(`Created ${data.code}`);
      try { await api.delete("/inline/drafts/quote"); } catch { /* silent */ }
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={guardedClose}
      data-testid="new-quote-drawer">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-4xl bg-white h-full overflow-auto p-6">
        {priceHist && <PriceHistoryPanel line={priceHist} customer={customer} currency={currency} excludeQuoteId={editQuote?.id} onClose={() => setPriceHist(null)}
          onApply={priceHist.__idx != null ? (row) => applyHistoryPrice(priceHist.__idx, row) : undefined} />}
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-heading font-semibold text-slate-900">
            {isEdit ? `Edit Quotation ${editQuote.code}` : "New Quotation"}
            <AdminChip area="quotation" label="Configure composer" testid="admin-chip-quote-composer" className="ml-2" />
          </h2>
          <div className="flex items-center gap-3">
            <span className="text-[11px]" data-testid="quote-draft-status">
              {draftStatus === "saving" && <span className="text-slate-400">Saving…</span>}
              {draftStatus === "saved" && <span className="text-emerald-600">Saved ✓</span>}
              {draftStatus === "error" && (
                <button className="text-red-600" onClick={() => { lastSavedRef.current = null; setDraftStatus("idle"); }}>
                  Save failed – Retry
                </button>
              )}
            </span>
            <button onClick={guardedClose} className="text-slate-500 hover:text-slate-900 text-[13px]"
              data-testid="new-quote-close">Close ✕</button>
          </div>
        </div>

        {recoverable && (
          <div className="mt-3 border border-amber-200 bg-amber-50 rounded-md px-3 py-2.5 flex flex-wrap items-center gap-2"
            data-testid="quote-draft-recover-banner">
            <div className="text-[13px] text-amber-900 min-w-0">
              <span className="font-medium">Recover draft?</span>{" "}
              Unsubmitted quote{recoverable.payload?.subject ? ` "${recoverable.payload.subject}"` : ""} —
              last saved {(recoverable.saved_at || "").slice(0, 16).replace("T", " ")} by {recoverable.user_email}
            </div>
            <div className="ml-auto flex gap-2">
              <button onClick={recoverDraft} data-testid="quote-draft-recover-btn"
                className="h-7 px-3 rounded bg-amber-600 text-white text-[12px] font-medium hover:bg-amber-700">
                Recover draft
              </button>
              <button onClick={discardDraft} data-testid="quote-draft-discard-btn"
                className="h-7 px-3 rounded border border-amber-300 text-amber-800 text-[12px] hover:bg-amber-100">
                Discard
              </button>
            </div>
          </div>
        )}

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div>
            <Label>Customer *</Label>
            <CustomerSelector value={customer} onChange={pickCustomer}
              onAddNew={(name) => { setAddInitialName(name || ""); setAddOpen(true); }} />
          </div>
          <div>
            <Label>Subject *</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Laptop refresh Q1 2026"
              data-testid="new-quote-subject" />
          </div>
          <div>
            <Label>Assigned to</Label>
            <select value={assignedToLabel} onChange={(e) => setAssignedToLabel(e.target.value)}
              data-testid="new-quote-assigned-to"
              className="w-full h-10 border border-slate-200 rounded-md px-2 text-sm bg-white">
              <option value="">— Select —</option>
              {ASSIGNED_TO_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div className="col-span-2 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Company display prefix</Label>
              <select value={displayPrefix} onChange={(e) => setDisplayPrefix(e.target.value)}
                data-testid="new-quote-display-prefix"
                className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white">
                <option value="M/s.">M/s.</option>
                <option value="None">None</option>
              </select>
              <div className="text-[10px] text-slate-400 mt-1">Shown before the company name; not part of the legal name</div>
            </div>
            <div>
              <Label>Contact title</Label>
              <select value={contactTitle} onChange={(e) => setContactTitle(e.target.value)}
                data-testid="new-quote-contact-title"
                className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white">
                {["None", "Mr.", "Ms.", "Mrs.", "Dr.", "Prof.", "Other"].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              {contactTitle === "Other" && (
                <Input value={otherTitle} onChange={(e) => setOtherTitle(e.target.value)}
                  placeholder="e.g. Adv. / CA" className="mt-1 h-8"
                  data-testid="new-quote-other-title" />
              )}
            </div>
            <div>
              <Label>Contact name</Label>
              <Input value={customerName} onChange={(e) => setCustomerName(e.target.value)}
                data-testid="new-quote-contact"
                placeholder={customer?.contact_name || "e.g. Priya Sharma"} />
              {looksLikePhone(customerName) && (
                <div className="mt-1 text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex items-center justify-between gap-2"
                  data-testid="contact-name-phone-hint">
                  <span>That looks like a phone number.</span>
                  <button type="button"
                    onClick={() => {
                      setCustomerPhone((prev) => prev?.trim() ? prev : customerName.trim());
                      setCustomerName("");
                    }}
                    className="font-semibold text-amber-900 hover:underline whitespace-nowrap"
                    data-testid="contact-name-move-phone">
                    Move to Contact number →
                  </button>
                </div>
              )}
            </div>
            <div>
              <Label>Contact email</Label>
              <Input value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)}
                data-testid="new-quote-email"
                placeholder={customer?.email || "name@customer.com"} />
            </div>
            <div>
              <Label>Contact number</Label>
              <Input value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)}
                data-testid="new-quote-phone"
                placeholder={customer?.phone || customer?.mobile || "e.g. +91 98765 43210"} />
            </div>
            <div>
              <Label>Designation</Label>
              <Input value={designation} onChange={(e) => setDesignation(e.target.value)}
                data-testid="new-quote-designation"
                placeholder="e.g. Purchase Manager" />
            </div>
          </div>
          <div>
            <Label>Currency</Label>
            <select value={currency} onChange={(e) => setCurrency(e.target.value)}
              className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white"
              data-testid="new-quote-currency">
              {["INR", "USD", "EUR", "GBP", "AED", "SGD"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <Label>Bank details on PDF</Label>
            <select value={bankDisplay} onChange={(e) => setBankDisplay(e.target.value)}
              className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white"
              data-testid="new-quote-bank-display">
              <option value="AUTO">Auto (match currency)</option>
              <option value="INR">INR accounts only</option>
              <option value="USD">USD accounts only</option>
              <option value="BOTH">Show both INR &amp; USD</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Payment terms</Label>
              <Input value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)}
                placeholder="e.g. Net 30" data-testid="new-quote-payment-terms" />
            </div>
            <div>
              <Label>Valid until</Label>
              <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)}
                data-testid="new-quote-valid-until" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Delivery schedule <span className="font-normal text-slate-400">(printed in T&amp;C + cover message)</span></Label>
              <DeliveryScheduleSelect value={deliverySchedule} onChange={setDeliverySchedule} testId="new-quote-delivery-schedule" />
            </div>
            <div>
              <Label>Price validity <span className="font-normal text-slate-400">(printed in T&amp;C + cover message)</span></Label>
              <PriceValiditySelect value={priceValidity} onChange={setPriceValidity} testId="new-quote-price-validity" />
            </div>
          </div>
          {defaultsApplied && (
            <div className="col-span-2 -mt-1" data-testid="quote-defaults-chip">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
                ✓ Currency, payment terms &amp; validity prefilled from {customer?.account_name}&apos;s profile — editable per quote
              </span>
            </div>
          )}
        </div>

        {customer && (
          <div className="mt-4 border border-emerald-200 bg-emerald-50/40 rounded-md p-3"
            data-testid="customer-summary">
            {(customer.gst_treatment && customer.gst_treatment !== "REGULAR")
              || (customer.sez_profile?.gst_treatment
                     && customer.sez_profile.gst_treatment !== "REGULAR") ? (
              <SezTaxChip customer={customer} />
            ) : null}
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[13px] text-slate-900">
                  <OrgName name={customer.account_name} testid="quote-selected-customer-name" />
                </div>
                <div className="text-[11px] font-mono text-slate-600 mt-0.5">
                  {customer.customer_code}
                  {customer.gstin ? ` · ${customer.gstin}` : ""}
                </div>
              </div>
              <button onClick={() => { setCustomer(null); setBillingAddress(""); setShippingAddress(""); setCustomerName(""); setCustomerEmail(""); setCustomerPhone(""); setIntel(null); }}
                className="text-[11px] text-rose-700 hover:text-rose-900"
                data-testid="clear-customer">
                Change customer
              </button>
            </div>

            {/* Commercial intelligence — sourced from real quote history only */}
            {intel && (intel.last_quote || intel.last_accepted) && (
              <div className="mt-2 border border-slate-200 bg-white rounded p-2 flex flex-wrap gap-4"
                data-testid="commercial-intel">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 w-full">
                  Recent commercial history · internal only
                </div>
                {intel.last_quote && (
                  <div className="text-[11px]">
                    <div className="text-slate-500">Last quote</div>
                    <div className="font-mono">{intel.last_quote.code}</div>
                    <div className="text-slate-500">
                      {intel.last_quote.date ? new Date(intel.last_quote.date).toLocaleDateString() : "—"}
                      {" · "}
                      {intel.last_quote.grand_total_inr != null
                        ? fmtINR(intel.last_quote.grand_total_inr) : "—"}
                    </div>
                  </div>
                )}
                {intel.last_accepted && (
                  <div className="text-[11px]">
                    <div className="text-emerald-700 font-medium">Last accepted</div>
                    <div className="font-mono">{intel.last_accepted.code}</div>
                    <div className="text-slate-500">
                      {intel.last_accepted.date ? new Date(intel.last_accepted.date).toLocaleDateString() : "—"}
                      {" · "}
                      {fmtINR(intel.last_accepted.grand_total_inr)}
                    </div>
                  </div>
                )}
                {intel.payment_terms && (
                  <div className="text-[11px]">
                    <div className="text-slate-500">Payment terms</div>
                    <div>{intel.payment_terms}</div>
                  </div>
                )}
                {intel.currency && (
                  <div className="text-[11px]">
                    <div className="text-slate-500">Currency</div>
                    <div>{intel.currency}</div>
                  </div>
                )}
              </div>
            )}

            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <div><div className="text-slate-500 uppercase text-[9px]">GSTIN</div><div className="font-mono">{customer.gstin || "—"}</div></div>
              <div><div className="text-slate-500 uppercase text-[9px]">City</div><div>{customer.billing_city || "—"}</div></div>
              <div><div className="text-slate-500 uppercase text-[9px]">State</div><div>{customer.billing_state || "—"}</div></div>
              <div><div className="text-slate-500 uppercase text-[9px]">Country</div><div>{customer.billing_country || "—"}</div></div>
            </div>

            {/* Primary contact — auto-selected from the customer; no re-entry */}
            <div className="mt-3">
              <div className="flex items-center gap-2 mb-1">
                <div className="text-[11px] font-semibold text-slate-700">{contact?.is_primary ? "Primary contact" : "Quote contact"}</div>
                {contact?.is_primary && (
                  <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800"
                    data-testid="contact-auto-badge">AUTO-SELECTED</span>
                )}
                {!contact?.is_primary && contact?.suggested_reason === "ROLE_MATCH" && contact?.matched_role && (
                  <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded bg-sky-100 text-sky-800"
                    data-testid="contact-suggested-badge">SUGGESTED · {contact.matched_role}</span>
                )}
                {!contact?.is_primary && contact?.suggested_reason === "COMMUNICATION_CONTACT" && (
                  <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded bg-sky-100 text-sky-800"
                    data-testid="contact-commcontact-badge">QUOTE CONTACT</span>
                )}
                {contact?.synthesised && (
                  <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800"
                    data-testid="contact-synth-badge">FROM CUSTOMER RECORD</span>
                )}
                {contact?.ambiguous && !contact?.is_primary && (
                  <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800"
                    data-testid="contact-ambiguous-badge">⚠ CHECK CONTACT — no primary set</span>
                )}
              </div>
              <ContactSelector customerId={customer.id} value={contact}
                onChange={pickContact}
                onAddNew={() => { setEditContact(null); setAddContactOpen(true); }} />
              {contact && (
                <div className="mt-1 text-[10px] text-slate-500 flex items-center justify-between">
                  <span>
                    {resolvedTitle ? `${resolvedTitle} ` : ""}
                    {contact.designation ? `${contact.designation}` : ""}
                    {contact.designation && contact.department ? " · " : ""}
                    {contact.department || ""}
                    {contact.phone ? ` · ${contact.phone}` : ""}
                    {contact.mobile ? ` · ${contact.mobile}` : ""}
                  </span>
                  <span className="flex items-center gap-2">
                    <button onClick={() => { setContact(null); setCustomerName(""); setCustomerEmail(""); setCustomerPhone(""); setDesignation(""); }}
                      className="text-slate-600 hover:underline"
                      data-testid="clear-contact">Change contact ▾</button>
                    {contact?.id && (
                      <button onClick={() => { setEditContact(contact); setAddContactOpen(true); }}
                        className="text-slate-600 hover:underline"
                        data-testid="edit-selected-contact">Edit contact</button>
                    )}
                    <button onClick={() => { setEditContact(null); setAddContactOpen(true); }}
                      className="text-emerald-700 hover:underline"
                      data-testid="add-another-contact">+ Add another contact</button>
                  </span>
                </div>
              )}
            </div>

            <div className="mt-3 flex items-center justify-between">
              <div className="text-[11px] font-semibold text-slate-700">Delivery / quote address</div>
              <label className="text-[11px] text-slate-600 flex items-center gap-1 cursor-pointer"
                data-testid="override-address-toggle">
                <input type="checkbox" checked={overrideAddress}
                  onChange={(e) => setOverrideAddress(e.target.checked)} />
                Override for this quote only
              </label>
            </div>
            <div className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px]">
              <div>
                <div className="text-slate-500 uppercase text-[9px] mb-0.5">Billing address</div>
                <Textarea rows={2} value={billingAddress}
                  disabled={!overrideAddress}
                  onChange={(e) => setBillingAddress(e.target.value)}
                  className="text-[11px] font-mono"
                  data-testid="billing-address" />
              </div>
              <div>
                <div className="text-slate-500 uppercase text-[9px] mb-0.5">Shipping address</div>
                <Textarea rows={2} value={shippingAddress}
                  disabled={!overrideAddress}
                  onChange={(e) => setShippingAddress(e.target.value)}
                  className="text-[11px] font-mono"
                  data-testid="shipping-address" />
              </div>
            </div>
            {overrideAddress && (
              <div className="mt-1 text-[10px] text-amber-800">
                Address change applies to this quotation only — Customer Master is not updated.
              </div>
            )}
          </div>
        )}

        <div className="mt-6">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 whitespace-nowrap">Line items</div>
            <div className="flex flex-wrap items-center gap-2" data-testid="new-quote-line-tools">
              <Button size="sm" variant="outline" onClick={() => setInlineProduct(true)} data-testid="new-quote-add-product">
                <Plus className="h-3 w-3 mr-1" /> Add Product
              </Button>
              <Button size="sm" variant="outline" onClick={() => setPriceHist({})} data-testid="new-quote-price-history">
                <History className="h-3 w-3 mr-1" /> Previous quoted prices
              </Button>
            </div>
          </div>

          {/* Column headers — spec §7: labelled columns replace unlabeled inputs */}
          <div className="grid grid-cols-12 gap-2 px-3 pb-1 text-[9px] uppercase tracking-wider text-slate-500">
            <div className="col-span-3">Product</div>
            <div className="col-span-1 text-right">Qty</div>
            <div className="col-span-2 text-right" title="Per unit, before discount. Choose Excluding / Including GST under each line.">Unit price / unit</div>
            <div className="col-span-1 text-right">Vendor cost</div>
            <div className="col-span-1 text-right">GST %</div>
            <div className="col-span-1 text-right">Disc %</div>
            <div className="col-span-2 text-right">Total (incl GST)</div>
            <div className="col-span-1" />
          </div>

          <div className="border border-slate-200 rounded-md divide-y">
            {items.map((it, i) => (
              <div key={i} className="p-3 space-y-2"
                data-testid={`new-quote-line-${i}`}>
                <div className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-3">
                    <ProductPicker
                      value={it}
                      index={i}
                      onSelect={(p) => {
                        // Auto-populate from Product Master.  Keep the
                        // line description separate from master so any
                        // edit is quote-scoped only (spec §8).
                        upd(i, "line_type",         p.line_type || "PRODUCT");
                        upd(i, "service_id",       p.service_id || null);
                        upd(i, "service_code",  p.service_code || null);
                        upd(i, "unit_of_measure", p.unit_of_measure || "UNIT");
                        upd(i, "product_id",       p.line_type === "SERVICE" ? null : p.id);
                        upd(i, "product_code",  p.product_code || p.sku);
                        upd(i, "product_name",  p.product_name);
                        upd(i, "part_number",     p.oem_part_number || p.part_number || null);
                        upd(i, "description",       p.description || "");
                        upd(i, "hsn_sac",             _cleanHsn(p.hsn_sac));
                        if (p.standard_selling_price != null)
                          upd(i, "unit_price_inr", p.standard_selling_price);
                        if (p.gst_percent != null)
                          upd(i, "tax_pct", p.gst_percent);
                      }} />
                    {it.product_id && (it.line_type || "PRODUCT") !== "SERVICE" && (
                      <div className="flex items-center gap-1.5 mt-1.5" data-testid={`line-preview-${i}`}>
                        {lineMeta[it.product_id]?.url ? (
                          <img src={lineMeta[it.product_id].url} alt="" loading="lazy"
                            className="h-9 w-9 object-contain rounded border border-slate-200 bg-white shrink-0"
                            data-testid={`line-thumb-${i}`} />
                        ) : (
                          <div className="h-9 w-9 rounded border border-dashed border-slate-200 bg-slate-50 flex items-center justify-center shrink-0"
                            title="No product image in master" data-testid={`line-thumb-empty-${i}`}>
                            <Package className="h-3.5 w-3.5 text-slate-300" />
                          </div>
                        )}
                        {lineMeta[it.product_id]?.colour && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 truncate max-w-[110px]"
                            title={lineMeta[it.product_id].colour}
                            data-testid={`line-colour-${i}`}>
                            {lineMeta[it.product_id].colour}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <Input className="col-span-1 h-8 text-[12px] text-right"
                    aria-label="Quantity" data-testid={`new-quote-qty-${i}`}
                    type="number" value={it.quantity}
                    onChange={(e) => upd(i, "quantity", e.target.value)} />
                  <GstPriceInput it={it} i={i} onPatch={(p) => patchLine(i, p)} />
                  <Input className="col-span-1 h-8 text-[12px] text-right"
                    aria-label="Vendor cost" data-testid={`new-quote-cost-${i}`}
                    type="number" value={it.vendor_unit_cost_inr}
                    onChange={(e) => upd(i, "vendor_unit_cost_inr", e.target.value)} />
                  <Input className="col-span-1 h-8 text-[12px] text-right"
                    aria-label="GST %" data-testid={`new-quote-tax-${i}`}
                    type="number" value={it.tax_pct}
                    onChange={(e) => upd(i, "tax_pct", e.target.value)} />
                  <Input className="col-span-1 h-8 text-[12px] text-right"
                    aria-label="Discount %" data-testid={`new-quote-disc-${i}`}
                    type="number" value={it.discount_pct}
                    onChange={(e) => upd(i, "discount_pct", e.target.value)} />
                  <div className="col-span-2 h-8 flex items-center justify-end px-2 rounded bg-slate-50 border border-slate-100 font-mono text-[12px] text-slate-800"
                    data-testid={`new-quote-line-total-${i}`}
                    title="(Unit price after discount + GST) × Qty">
                    {fmtMoney2(lineCalc(it).total, currency)}
                  </div>
                  <button onClick={() => rmLine(i)}
                    className="col-span-1 text-rose-600 hover:text-rose-800 justify-self-end"
                    data-testid={`new-quote-rm-${i}`}
                    aria-label="Remove line">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <GstPriceBreakdown it={it} i={i} currency={currency} onPatch={(p) => patchLine(i, p)} />
                {(it.product_id || (it.product_name || "").trim()) && <button type="button" onClick={() => setPriceHist({ ...it, __idx: i })} className="self-start text-[10.5px] font-semibold text-[#0077B0] hover:underline" data-testid={`new-quote-price-history-${i}`}>Previous quoted prices</button>}
                {/* Price-on-request guard — a real product with no price */}
                {(it.product_name || "").trim() && !(Number(it.unit_price_inr) > 0) && (
                  <div className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
                    data-testid={`new-quote-price-warn-${i}`}>
                    Price on request — set a unit price before sending, or this line quotes at ₹0.
                  </div>
                )}
                {/* Per-line description — populates from Product Master but
                    is quote-scoped (spec §8) */}
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-0.5">
                    Description {it.product_id && !it.description &&
                      <span className="text-rose-700 font-semibold">· Description required</span>}
                  </div>
                  <BoldableTextarea rows={2}
                    value={it.description || ""}
                    onChange={(val) => upd(i, "description", val)}
                    enableSmartFormat
                    smartContext={{ ref: it.product_code || it.part_number || it.product_name || "quote-line", brand: it.brand }}
                    placeholder="Product description that will appear on the PDF"
                    className="text-[12px]"
                    testid={`new-quote-desc-${i}`} />
                  <div className="text-[9px] text-slate-500 italic mt-0.5">
                    Editing this description only changes THIS quote; Product Master stays intact.
                  </div>
                </div>
                {/* HSN / SAC code — local lookup + AI suggestion (verified) */}
                <div className="flex items-center gap-2">
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 min-w-[70px]">HSN / SAC</div>
                  <div className="w-[300px] max-w-full">
                    <HsnPicker
                      value={it.hsn_sac}
                      productName={it.product_name}
                      productDesc={it.description}
                      type={(it.line_type || "").toUpperCase() === "SERVICE" ? "SAC" : "HSN"}
                      onChange={(code) => upd(i, "hsn_sac", code || null)}
                      testid={`new-quote-hsn-${i}`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2 flex justify-start">
            <Button size="sm" variant="outline" onClick={addLine}
              data-testid="new-quote-add-line">
              <Plus className="h-3 w-3 mr-1" /> Add line
            </Button>
          </div>
          {inlineProduct && <AddProductModal context="Quotation" onClose={() => setInlineProduct(false)}
            onCreated={(p) => { if (p?.id && !items.some((x) => x.product_id === p.id)) setItems((s) => [...s.filter((x) => (x.product_name || "").trim() || x.product_id), { product_id: p.id, product_name: p.product_name, part_number: p.oem_part_number || p.product_code || "", description: p.short_description || p.description || "", hsn_sac: p.hsn_sac, quantity: 1, unit_price_inr: Number(p.standard_selling_price) || 0, vendor_unit_cost_inr: 0, tax_pct: p.gst_percent == null || p.gst_percent === "" ? 18 : Number(p.gst_percent), discount_pct: 0 }]); setInlineProduct(false); toast.success(`${p.product_name || "Product"} added to this quote`); }} />}
        </div>

        <div className="mt-5 border-t pt-3 space-y-1 text-[13px]">
          <div className="flex justify-between"><span className="text-slate-500">Subtotal (excl. GST)</span><span className="font-mono" data-testid="new-quote-subtotal">{fmtMoney2(totals.subtotal, currency)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">GST</span><span className="font-mono" data-testid="new-quote-tax">{fmtMoney2(totals.tax, currency)}</span></div>
          <div className="flex justify-between text-[16px] font-semibold pt-1 border-t">
            <span>Grand total</span>
            <span className="font-mono text-slate-900" data-testid="new-quote-total">{fmtMoney2(totals.total, currency)}</span>
          </div>
        </div>

        {items.filter((it) => (it.product_name || "").trim() && !(Number(it.unit_price_inr) > 0)).length > 0 && (
          <div className="mt-4 text-[12px] text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
            data-testid="price-on-request-banner">
            {items.filter((it) => (it.product_name || "").trim() && !(Number(it.unit_price_inr) > 0)).length} line
            {items.filter((it) => (it.product_name || "").trim() && !(Number(it.unit_price_inr) > 0)).length === 1 ? " has" : "s have"} no
            price (Price on request). Set a unit price before sending so nothing is quoted at ₹0.
          </div>
        )}

        <div className="mt-5 flex gap-2">
          <Button variant="outline" onClick={discardAndClose} className="flex-1 h-10" data-testid="quote-editor-cancel">
            <Trash2 className="h-4 w-4 mr-1.5" /> {isEdit ? "Discard changes" : "Discard"}
          </Button>
          <Button variant="create" onClick={submit} disabled={busy}
            className="flex-1 h-10"
            data-testid="new-quote-submit">
            <CheckCircle2 className="h-4 w-4 mr-1.5" />
            {busy ? (isEdit ? "Saving…" : "Creating…") : (isEdit ? "Save & Close" : "Create quotation")}
          </Button>
        </div>
      </div>
      {addOpen && (
        <AddCustomerModal initialName={addInitialName} onClose={() => setAddOpen(false)}
          onCreated={(c) => { setAddOpen(false); pickCustomer(c); }} />
      )}
      {addContactOpen && customer && (
        <AddContactModal customerId={customer.id} editContact={editContact}
          onClose={() => { setAddContactOpen(false); setEditContact(null); }}
          onCreated={(ct) => { setAddContactOpen(false); setEditContact(null); pickContact(ct); }} />
      )}
    </div>
  );
}


/** Searchable customer autocomplete against Customer Master (crm_accounts). */
function CustomerSelector({ value, onChange, onAddNew }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/vtiger/crm-customers",
          { params: { q: q || undefined, limit: 25 } });
        if (!cancelled) setRows(data.items || []);
      } catch (e) { if (!cancelled) setRows([]); }
      finally { if (!cancelled) setBusy(false); }
    }, 200);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [q]);

  if (value) {
    // Compact "selected" chip when a customer is already picked.
    return (
      <div className="w-full h-9 flex items-center justify-between px-2 border border-emerald-300 bg-emerald-50/60 rounded-md"
        data-testid="customer-picked">
        <div className="min-w-0 flex-1">
          <div className="text-[13px] text-slate-900 truncate"><OrgName name={value.account_name} /></div>
          <div className="text-[10px] font-mono text-slate-500 truncate">{value.customer_code}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative" data-testid="customer-selector">
      <Input placeholder="Search name, code, GSTIN, legacy ID, city…"
        value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => { setFocused(true); setOpen(true); }}
        onBlur={() => { setFocused(false); setTimeout(() => setOpen(false), 180); }}
        data-testid="customer-search" />
      {open && (
        <div className="absolute z-40 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-80 overflow-y-auto"
          data-testid="customer-dropdown">
          {busy && (
            <div className="p-3 text-[11px] text-slate-500">Searching…</div>
          )}
          {!busy && rows.length === 0 && (
            <div className="p-3 text-[11px] text-slate-500">
              No customer matches “{q}”.
              <button onClick={() => onAddNew(q)}
                className="ml-1 text-emerald-700 font-medium hover:underline"
                data-testid="add-customer-empty">
                + Add Customer
              </button>
            </div>
          )}
          {rows.map(r => (
            <button key={r.id}
              onMouseDown={(e) => { e.preventDefault(); onChange(r); setQ(""); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-emerald-50/70 border-b border-slate-50"
              data-testid={`customer-opt-${r.id}`}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[13px] text-slate-900 truncate"><OrgName name={r.account_name} /></span>
                <span className="text-[10px] font-mono text-slate-500">{r.customer_code}</span>
              </div>
              <div className="text-[10px] text-slate-500 truncate">
                {[r.customer_code, r.gstin, r.billing_city, r.billing_country]
                  .filter(Boolean).join(" · ") || (r.email || "—")}
              </div>
            </button>
          ))}
          {!busy && rows.length > 0 && (
            <button onMouseDown={(e) => { e.preventDefault(); onAddNew(q); }}
              className="w-full text-left px-3 py-2 text-[12px] font-medium text-emerald-800 hover:bg-emerald-50 border-t border-emerald-200"
              data-testid="add-customer-footer">
              + Add Customer
            </button>
          )}
        </div>
      )}
    </div>
  );
}


function ContactSelector({ customerId, value, onChange, onAddNew }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!customerId) { setRows([]); return; }
    let cancelled = false;
    setBusy(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/vtiger/crm-contacts",
          { params: { customer_id: customerId, q: q || undefined, limit: 100 } });
        if (!cancelled) setRows(data.items || []);
      } catch (e) { if (!cancelled) setRows([]); }
      finally { if (!cancelled) setBusy(false); }
    }, 180);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [customerId, q]);

  if (value) {
    return (
      <div className="w-full min-h-9 flex items-center justify-between px-2 py-1 border border-emerald-300 bg-emerald-50/60 rounded-md"
        data-testid="contact-picked">
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold text-slate-900 truncate">{value.full_name || value.name || "(unnamed contact)"}</div>
          <div className="text-[10px] text-slate-600 truncate">
            {[value.email, value.mobile || value.phone].filter(Boolean).join(" · ") || "no email / phone"}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative" data-testid="contact-selector">
      <Input placeholder="Search contact by name, email, designation…"
        value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        data-testid="contact-search" />
      {open && (
        <div className="absolute z-40 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-y-auto"
          data-testid="contact-dropdown">
          {busy && (<div className="p-3 text-[11px] text-slate-500">Searching…</div>)}
          {!busy && rows.length === 0 && (
            <div className="p-3 text-[11px] text-slate-500">
              No contacts under this customer{q ? ` match “${q}”` : ""}.
              <button onClick={() => onAddNew()}
                className="ml-1 text-emerald-700 font-medium hover:underline"
                data-testid="add-contact-empty">
                + Add Contact
              </button>
            </div>
          )}
          {rows.map(r => (
            <button key={r.id}
              onMouseDown={(e) => { e.preventDefault(); onChange(r); setQ(""); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-emerald-50/70 border-b border-slate-50"
              data-testid={`contact-opt-${r.id}`}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[13px] font-semibold text-slate-900 truncate">{r.full_name}</span>
                <span className="text-[10px] text-slate-500">{r.designation || ""}</span>
              </div>
              <div className="text-[10px] text-slate-500 truncate">
                {[r.email, r.mobile || r.phone, r.department].filter(Boolean).join(" · ") || "—"}
              </div>
            </button>
          ))}
          {!busy && rows.length > 0 && (
            <button onClick={() => onAddNew()}
              className="w-full text-left px-3 py-2 text-[12px] font-medium text-emerald-800 hover:bg-emerald-50 border-t border-emerald-200"
              data-testid="add-contact-footer">
              + Add Contact
            </button>
          )}
        </div>
      )}
    </div>
  );
}


function CoverEmailButton({ quote }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [to, setTo] = useState(quote.contact_email || quote.customer_email || "");
  const [attachProfile, setAttachProfile] = useState(false);
  const [open, setOpen] = useState(false);
  const send = async (mode) => {
    if (!to) { toast.error("Enter a recipient email"); return; }
    setBusy(true);
    try {
      const { data } = await postWithPresend(`/vtiger/quotations/${quote.id}/cover-email`,
        { to, mode, attach_quote_pdf: true, attach_profile: attachProfile }, user?.role);
      toast.success(data.status === "SENT" || data.status === "ALREADY_SENT"
        ? `Cover email sent (${data.attachments} attachment${data.attachments === 1 ? "" : "s"})`
        : `Outlook draft created (${data.attachments} attachment${data.attachments === 1 ? "" : "s"})`);
      setOpen(false);
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === "string" ? det : (!e.response && e.message) || "Outlook sending is not configured yet");
    }
    setBusy(false);
  };
  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="cover-email-panel">
      {!open ? (
        <Button variant="comms" size="sm" onClick={() => setOpen(true)} data-testid="cover-email-open">
          <Mail className="h-4 w-4 mr-1.5" /> Send cover email via Outlook
        </Button>
      ) : (
        <div className="space-y-2">
          <div className="text-[12px] font-medium text-slate-700">Quote cover email · Verdana 12pt · PDF attached</div>
          <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Recipient email" data-testid="cover-email-to" className="max-w-sm" />
          <label className="flex items-center gap-1.5 text-[12px] text-slate-600">
            <input type="checkbox" checked={attachProfile} onChange={(e) => setAttachProfile(e.target.checked)} data-testid="cover-email-profile" />
            Also attach SUNTRONIX company profile PDF
          </label>
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" onClick={() => send("DRAFT")} disabled={busy} data-testid="cover-email-draft">Create Outlook draft</Button>
            <Button variant="comms" size="sm" className="" onClick={() => send("SEND")} disabled={busy} data-testid="cover-email-send">Send now</Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}


function IssueQuoteButton({ quote, onIssued }) {
  const [open, setOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [cc, setCc] = useState([]);   // contact ids to CC
  const [busy, setBusy] = useState(false);
  const toEmail = quote.customer_email || quote.customer_contact_email || "";
  const toContactId = quote.customer_contact_id || null;

  const openModal = async () => {
    setOpen(true); setCc([]);
    if (quote.customer_account_id) {
      try {
        const { data } = await api.get("/vtiger/crm-contacts",
          { params: { customer_id: quote.customer_account_id, limit: 100 } });
        setContacts((data.items || []).filter((c) => c.email));
      } catch { setContacts([]); }
    }
  };
  const toggle = (id) => setCc((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const ccCandidates = () => contacts.filter((c) =>
    c.id !== toContactId && (c.email || "").toLowerCase() !== (toEmail || "").toLowerCase());

  const doIssue = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/vtiger/quotations/${quote.id}/issue`,
        cc.length ? { cc_contact_ids: cc } : {});
      const em = res.data?.email || {};
      const failed = em.attempted && (em.error || (em.status && !["SENT", "MOCKED_LOGGED"].includes(em.status)));
      if (failed) {
        toast.warning(`Issued v${res.data.version}, but the email did not send (${em.status || "error"}). Snapshot saved.`);
      } else {
        toast.success(`Issued v${res.data.version}${em.cc?.length ? ` · CC ${em.cc.length}` : ""}`);
      }
      setOpen(false); onIssued();
    } catch (e) { toast.error(e.response?.data?.detail || "Issue failed"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" onClick={openModal}
        className="border-emerald-500 text-emerald-800 hover:bg-emerald-50" data-testid="quote-issue">
        <CheckCircle2 className="h-4 w-4 mr-1.5" /> Issue Quote
      </Button>
      {open && (
        <div className="fixed inset-0 z-[60] bg-slate-900/50 flex items-start justify-center overflow-y-auto p-4"
          data-testid="issue-quote-modal" onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-2xl w-full max-w-lg mt-16">
            <div className="p-4 border-b border-slate-200">
              <div className="text-sm font-heading font-semibold">Issue &amp; send quotation</div>
              <div className="text-[11px] text-slate-500 mt-1">
                Freezes an immutable PDF snapshot and emails it. Future edits need a fresh Issue.
              </div>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-[12px]">
                <span className="text-slate-500">To: </span>
                <span className="font-medium text-slate-800" data-testid="issue-to">{toEmail || "— no contact email —"}</span>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">CC other contacts (optional)</div>
                {ccCandidates().length === 0 ? (
                  <div className="text-[12px] text-slate-400">No other contacts with an email for this customer.</div>
                ) : (
                  <div className="border border-slate-200 rounded-md divide-y max-h-52 overflow-y-auto" data-testid="issue-cc-list">
                    {ccCandidates().map((c) => (
                      <label key={c.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50"
                        data-testid={`issue-cc-${c.id}`}>
                        <input type="checkbox" checked={cc.includes(c.id)} onChange={() => toggle(c.id)} />
                        <span className="text-[12px] text-slate-800"><PersonName name={c.full_name || c.name} /></span>
                        <span className="text-[10px] text-slate-500 ml-auto">
                          {(c.contact_roles || []).filter((r) => r !== "PRIMARY").join(", ")} · {c.email}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="p-4 border-t border-slate-200 flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={doIssue} disabled={busy}
                className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="issue-confirm">
                {busy ? "Issuing…" : "Issue & send"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


const QUOTE_CONTACT_ROLES = ["PRIMARY", "SALES", "PROCUREMENT", "IT", "FINANCE",
  "ACCOUNTS", "MANAGEMENT", "DELIVERY", "TECHNICAL", "ADMINISTRATION", "OTHER"];

function AddContactModal({ customerId, editContact, onClose, onCreated }) {
  const isEdit = !!(editContact && editContact.id);
  const [form, setForm] = useState({
    salutation: editContact?.salutation || editContact?.title || "None",
    first_name: editContact?.first_name || "", middle_name: editContact?.middle_name || "",
    last_name: editContact?.last_name || "", designation: editContact?.designation || "",
    department: editContact?.department || "",
    contact_roles: (editContact?.contact_roles && editContact.contact_roles.length) ? editContact.contact_roles : [],
    email: editContact?.email || "", email2: editContact?.email2 || "",
    phone: editContact?.phone || "", mobile: editContact?.mobile || "",
    alt_phone: editContact?.alt_phone || "", notes: editContact?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [dupe, setDupe] = useState(null);  // possible existing contact (spec §16)
  const upd = (k, v) => setForm(s => ({ ...s, [k]: v }));
  const toggleRole = (r) => setForm((s) => {
    const has = (s.contact_roles || []).includes(r);
    return { ...s, contact_roles: has ? s.contact_roles.filter((x) => x !== r) : [...(s.contact_roles || []), r] };
  });

  // Duplicate detection — fires when email or mobile leaves the field.
  const checkDupe = async () => {
    if (isEdit) return;
    const email = form.email.trim(), mobile = form.mobile.trim();
    if (!email && !mobile) { setDupe(null); return; }
    try {
      const { data } = await api.get("/vtiger/crm-contacts/check-duplicate",
        { params: { customer_id: customerId, email: email || undefined, mobile: mobile || undefined } });
      setDupe(data.match || null);
    } catch { setDupe(null); }
  };

  const save = async () => {
    if (!(form.first_name.trim() || form.last_name.trim() || form.email.trim())) {
      toast.error("Give a name or an email"); return;
    }
    setBusy(true);
    try {
      const payload = { ...form, salutation: form.salutation === "None" ? null : form.salutation,
        contact_roles: form.contact_roles && form.contact_roles.length ? form.contact_roles : undefined };
      let data;
      if (isEdit) {
        ({ data } = await api.patch(`/vtiger/crm-contacts/${editContact.id}`, payload));
        toast.success("Contact updated");
      } else {
        ({ data } = await api.post("/vtiger/crm-contacts", { customer_id: customerId, ...payload }));
        toast.success("Contact added");
      }
      onCreated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/50 flex items-start justify-center overflow-y-auto p-4"
      data-testid="add-contact-modal" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-2xl w-full max-w-lg mt-16">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="text-sm font-heading font-semibold">{isEdit ? "Edit contact" : "Add another contact"}</div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900 text-[13px]"
            data-testid="add-contact-close">Close ✕</button>
        </div>
        {dupe && !isEdit && (
          <div className="mx-4 mt-3 p-3 rounded-md border border-amber-300 bg-amber-50 text-[12px]" data-testid="dupe-banner">
            <div className="font-semibold text-amber-900">Possible existing contact</div>
            <div className="text-amber-800 mt-0.5">
              {dupe.full_name || dupe.name} · {[dupe.email, dupe.mobile || dupe.phone].filter(Boolean).join(" · ")}
              {!dupe.same_customer && <span className="ml-1 font-medium">(under a different customer)</span>}
            </div>
            <div className="mt-2 flex gap-3">
              {dupe.same_customer && (
                <button onClick={() => onCreated(dupe)} className="text-emerald-800 font-medium hover:underline" data-testid="dupe-use-existing">Use existing</button>
              )}
              <button onClick={() => setDupe(null)} className="text-slate-600 hover:underline" data-testid="dupe-dismiss">Continue anyway</button>
            </div>
          </div>
        )}
        <div className="p-4 grid grid-cols-2 gap-3">
          <div><Label>Salutation</Label>
            <select value={form.salutation} onChange={(e) => upd("salutation", e.target.value)}
              data-testid="add-contact-salutation"
              className="w-full h-9 text-[13px] border border-slate-200 rounded-md px-2">
              {["None", "Mr.", "Ms.", "Mrs.", "Dr.", "Prof.", "Other"].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div><Label>Designation</Label><Input value={form.designation} onChange={(e) => upd("designation", e.target.value)} data-testid="add-contact-designation" /></div>
          <div><Label>First name</Label><Input value={form.first_name} onChange={(e) => upd("first_name", e.target.value)} data-testid="add-contact-first" /></div>
          <div><Label>Middle name</Label><Input value={form.middle_name} onChange={(e) => upd("middle_name", e.target.value)} data-testid="add-contact-middle" /></div>
          <div><Label>Last name</Label><Input value={form.last_name} onChange={(e) => upd("last_name", e.target.value)} data-testid="add-contact-last" /></div>
          <div><Label>Department</Label><Input value={form.department} onChange={(e) => upd("department", e.target.value)} data-testid="add-contact-department" /></div>
          <div className="col-span-2"><Label>Email</Label><Input value={form.email} onChange={(e) => upd("email", e.target.value)} onBlur={checkDupe} data-testid="add-contact-email" /></div>
          <div><Label>Alternate email</Label><Input value={form.email2} onChange={(e) => upd("email2", e.target.value)} data-testid="add-contact-email2" /></div>
          <div><Label>Mobile</Label><Input value={form.mobile} onChange={(e) => upd("mobile", e.target.value)} onBlur={checkDupe} data-testid="add-contact-mobile" /></div>
          <div><Label>Alternate phone</Label><Input value={form.alt_phone} onChange={(e) => upd("alt_phone", e.target.value)} data-testid="add-contact-altphone" /></div>
          <div className="col-span-2">
            <Label>Contact roles</Label>
            <div className="flex flex-wrap gap-1.5 mt-1" data-testid="add-contact-roles">
              {QUOTE_CONTACT_ROLES.map((r) => {
                const on = (form.contact_roles || []).includes(r);
                return (
                  <button key={r} type="button" onClick={() => toggleRole(r)}
                    data-testid={`add-contact-role-${r}`}
                    className={`text-[10px] px-2 py-1 rounded-full border transition-colors ${on ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"}`}>
                    {r}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => upd("notes", e.target.value)} data-testid="add-contact-notes" /></div>
        </div>
        <div className="p-4 border-t border-slate-200 flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy}
            className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="add-contact-save">
            {busy ? "Saving…" : (isEdit ? "Save & use in quote" : "Save & use in quote")}
          </Button>
        </div>
      </div>
    </div>
  );
}



function QuoteVersionsPanel({ quotationId }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get(`/vtiger/quotations/${quotationId}/pdf/versions`)
      .then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
  }, [quotationId]);
  if (!items.length) return null;
  return (
    <div className="border border-slate-200 rounded-lg p-3" data-testid="quote-versions-panel">
      <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-1.5">
        Issued PDF versions &amp; email opens</div>
      {items.map((v) => (
        <div key={v.version} className="flex items-center gap-2 text-[11px] py-1 border-b border-slate-50 last:border-0"
            data-testid={`quote-version-${v.version}`}>
          <span className="font-mono font-semibold">v{v.version}</span>
          <span className="text-slate-500">{String(v.issued_at || "").slice(0, 16).replace("T", " ")}</span>
          <span className="flex-1 text-slate-400 truncate">{v.email_track?.to || ""}</span>
          {v.email_track ? (
            v.email_track.opened_at
              ? <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800"
                    data-testid={`quote-v${v.version}-opened`}>
                  OPENED {String(v.email_track.opened_at).slice(0, 16).replace("T", " ")}
                  {v.email_track.opens > 1 ? ` ×${v.email_track.opens}` : ""}</span>
              : <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">NOT OPENED YET</span>
          ) : <span className="text-[9px] text-slate-400">no email</span>}
          <a href={`${api.defaults?.baseURL || ""}/vtiger/quotations/${quotationId}/pdf/versions/${v.version}`}
              target="_blank" rel="noreferrer"
              className="text-[10px] text-emerald-800 underline">PDF</a>
        </div>))}
    </div>
  );
}

function QuoteDrawer({ q, costVisible, onClose, onSaved, onEdit, onFitChanged }) {
  const [status, setStatus] = useState(q.status);
  const [notes, setNotes] = useState(q.internal_notes || "");
  const [customerNotes, setCustomerNotes] = useState(q.customer_notes || "");
  const [busy, setBusy] = useState(false);
  const [showTechDocs, setShowTechDocs] = useState(false);
  const [techCount, setTechCount] = useState(0);
  const [techFilterPid, setTechFilterPid] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/vtiger/quotations/${q.id}/attachments`);
        setTechCount(data.count || 0);
      } catch { /* silent */ }
    })();
  }, [q.id]);
  const [acceptOpen, setAcceptOpen] = useState(null);   // 'ACCEPTED' | 'REJECTED' | 'REVISION_REQUESTED'
  const [flow, setFlow] = useState(null);
  const accepted = q.status === "ACCEPTED" || !!q.one_order_id || !!(q.one_order_ids || []).length || flow?.quote?.status === "ACCEPTED" || !!flow?.order;
  const [tab, setTab] = useState(accepted ? "operations" : "overview");
  useEffect(() => { if (accepted) setTab("operations"); }, [accepted]);
  const [pdfBusy, setPdfBusy] = useState(false);
  const showSales = !accepted || tab === "overview";

  const save = async () => {
    setBusy(true);
    try {
      const patch = { status, internal_notes: notes, customer_notes: customerNotes };
      await api.patch(`/vtiger/quotations/${q.id}`, patch);
      toast.success("Quotation updated");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const clone = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sales-desk/quotations/${q.id}/clone`);
      toast.success(`Cloned to ${data.code}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clone failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}
      data-testid="quote-drawer">
      <div onClick={(e) => e.stopPropagation()}
        className={`w-full ${accepted ? "max-w-6xl" : "max-w-3xl"} bg-white h-full overflow-auto p-6`} data-testid="quote-drawer-body" data-mode={accepted ? "quote-to-order" : "quote"}>
        {accepted && <TxnHeader s={flow?.summary} />}
        {accepted && flow?.order?.id && <div className="mb-3"><EndClientBar orderId={flow.order.id} /></div>}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 inline-flex flex-wrap items-center gap-2">
              <STXReferenceNumber value={q.code} copy size={13} />
              <CopyCoverMessageButton quote={q} className="normal-case tracking-normal" onCopied={onClose} />
            </div>
            <h2 className="text-2xl font-heading font-semibold text-slate-900 mt-1 break-words">{q.subject}</h2>
            <div className="text-[11px] text-slate-600 mt-1" data-testid="quote-drawer-date">
              <span className="font-semibold">Date:</span> {fmtQuoteDate(q)}
              {q.valid_until && <span className="ml-3"><span className="font-semibold">Valid Until:</span> {fmtQuoteDate({ quote_date: q.valid_until })}</span>}
              {q.delivery_schedule && <span className="ml-3" data-testid="quote-drawer-delivery-schedule"><span className="font-semibold">Delivery Schedule:</span> {q.delivery_schedule}</span>}
              {q.price_validity && <span className="ml-3" data-testid="quote-drawer-price-validity"><span className="font-semibold">Price Validity:</span> {q.price_validity}</span>}
            </div>
            <div className="flex gap-2 mt-2 flex-wrap">
              <Badge className={"text-[10px] border " + (STATUS_TONE[quoteStage(q)] || "")} data-testid="quote-drawer-stage">{quoteStage(q)}</Badge>
              <CommChips q={q} compact={false} />
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <SendWhatsAppButton record={{ kind: "quotation", id: q.id, code: q.code }} purpose="SALES" className="whitespace-nowrap" testid="send-whatsapp-quotation" />
            <button onClick={onClose} className="whitespace-nowrap text-slate-500 hover:text-slate-900 text-[13px]"
              data-testid="quote-drawer-close">Close ✕</button>
          </div>
        </div>

        {accepted && <QuoteTabs tab={tab} setTab={setTab} />}
        {showSales && <div className="mt-3"><QuoteExperiencePanel quote={q} /></div>}
        {!accepted && <FulfilmentNotActivated />}
        {accepted && <QuoteToOrderFlow quoteId={q.id} onChanged={onSaved} onFlow={setFlow} tab={tab} />}
        <div className={showSales ? "" : "hidden"} data-testid="quote-sales-tools">
        <div className="mt-4">
          <QuoteActionBar q={q} onChanged={onSaved} onEdit={onEdit} onFitChanged={onFitChanged} />
          <CoverEmailButton quote={q} />
          <SendQuoteWhatsApp quote={q} />
        </div>
        <QuoteGstinBanner quotationId={q.id} onChanged={onSaved} />
        <div className="mt-2"><EntityAddressStrip entityType="QUOTE" entityId={q.id} customerAccountId={q.customer_account_id} /></div>

        <div className="mt-4 rounded-md border border-slate-200 p-3">
          <RecordHistory collection="crm_quotations" recordId={q.id} title="Audit trail — who changed what" />
        </div>

        <div className="mt-4">
          <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-1">Commercial lifecycle</div>
          <CommercialLifecycleStrip
              record={{
                  quote_id: q.id,
                  customer_po_id: (q.customer_po_ids || [])[0] || q.customer_po_id,
                  one_order_id: (q.one_order_ids || [])[0] || q.one_order_id,
              }}
              currentStage={
                  q.one_order_ids?.length ? "one_order"
                      : q.customer_po_ids?.length ? "customer_po"
                      : "quotation"
              }
          />
        </div>

        <div className="mt-3">
          <ProcessGuidancePanel module="quotation" recordId={q.id} />
        </div>
        <div className="mt-3">
          <QuoteFollowUpPanel quotationId={q.id} />
        </div>
        <div className="mt-3">
          <QuoteVersionsPanel quotationId={q.id} />
          <DigitalQuotePanel quotationId={q.id} />
        </div>

        <div className="mt-3 flex items-center gap-2">
          <button onClick={clone} disabled={busy}
            data-testid="quote-clone-btn"
            className="text-[11px] px-2.5 py-1 rounded border border-slate-300 hover:border-emerald-400 hover:bg-emerald-50 text-slate-700">
            Clone quotation
          </button>
          <button onClick={() => { setTechFilterPid(null); setShowTechDocs(true); }}
            data-testid="quote-tech-docs-open"
            className="text-[11px] px-2.5 py-1 rounded border border-slate-300 hover:border-emerald-400 hover:bg-emerald-50 text-slate-700 flex items-center gap-1">
            <FileText className="h-3 w-3" />
            Tech documents {techCount > 0 && `(${techCount})`}
          </button>
          <a href={`${api.defaults?.baseURL || ""}/vtiger/quotations/${q.id}/pack`}
            data-testid="quote-download-pack"
            className="text-[11px] px-2.5 py-1 rounded border border-slate-300 hover:border-emerald-400 hover:bg-emerald-50 text-slate-700 flex items-center gap-1">
            <FileText className="h-3 w-3" />
            Download quote pack
          </a>
        </div>

        {q.vtiger_last_error && (
          <div className="mt-4 p-3 bg-rose-50 border border-rose-200 rounded-md flex items-start gap-2"
            data-testid="quote-sync-error">
            <AlertTriangle className="h-4 w-4 text-rose-700 mt-0.5" />
            <div>
              <div className="text-[12px] font-semibold text-rose-800">Legacy import note</div>
              <div className="text-[11px] text-rose-700 mt-0.5 font-mono">{q.vtiger_last_error}</div>
            </div>
          </div>
        )}

        {/* ---------- Customer Acceptance strip (only when issued at least once) ---------- */}
        {q.quote_version >= 1 && (
          <div className="mt-4 p-3 rounded-md border border-slate-200 bg-slate-50"
            data-testid="quote-acceptance-strip">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">
                  Customer Acceptance
                </div>
                <div className="text-[13px] mt-1 flex items-center gap-2">
                  {q.acceptance_status ? (
                    <>
                      <Badge className={"text-[10px] border " + (
                        q.acceptance_status === "ACCEPTED"
                          ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                          : q.acceptance_status === "REJECTED"
                          ? "bg-rose-50 text-rose-800 border-rose-200"
                          : "bg-amber-50 text-amber-800 border-amber-200"
                      )} data-testid="quote-acceptance-badge">{q.acceptance_status}</Badge>
                      <span className="text-[11px] text-slate-500">
                        {q.acceptance_recorded_by || "—"}
                        {q.acceptance_recorded_at && ` · ${new Date(q.acceptance_recorded_at).toLocaleDateString()}`}
                      </span>
                    </>
                  ) : (
                    <span className="text-[12px] text-slate-500">Awaiting customer response</span>
                  )}
                </div>
                {q.acceptance_notes && (
                  <div className="mt-1 text-[11px] text-slate-600 italic">&ldquo;{q.acceptance_notes}&rdquo;</div>
                )}
              </div>
              <div className="flex gap-2 flex-wrap justify-end">
                <Button size="sm" variant="outline" onClick={() => setAcceptOpen("ACCEPTED")}
                  className="border-emerald-500 text-emerald-800 hover:bg-emerald-50"
                  data-testid="quote-record-accept">
                  <ThumbsUp className="h-3.5 w-3.5 mr-1" /> Accept
                </Button>
                <Button size="sm" variant="outline" onClick={() => setAcceptOpen("REVISION_REQUESTED")}
                  className="border-amber-500 text-amber-800 hover:bg-amber-50"
                  data-testid="quote-record-revise">
                  <RotateCcw className="h-3.5 w-3.5 mr-1" /> Revision
                </Button>
                <Button size="sm" variant="outline" onClick={() => setAcceptOpen("REJECTED")}
                  className="border-rose-500 text-rose-800 hover:bg-rose-50"
                  data-testid="quote-record-reject">
                  <ThumbsDown className="h-3.5 w-3.5 mr-1" /> Reject
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-3"><RecordIntelStrip kind="quote" id={q.id} onRefreshKey={q.status} /></div>
        {!accepted && <QuoteToOrderFlow quoteId={q.id} onChanged={onSaved} onFlow={setFlow} tab="overview" />}

        <div className="mt-4 grid grid-cols-2 gap-3">
          <InfoTile label="Customer" value={q.customer_contact_name || q.customer_email || "—"} />
          <InfoTile label="Currency" value={q.currency} />
          <InfoTile label="Grand total" value={fmtMoney2(q.grand_total_inr, q.currency)} bold />
          {costVisible && (
            <InfoTile label="Gross margin"
              value={`${fmtMoney2(q.gross_margin_inr, q.currency)} (${q.margin_pct ?? 0}%)`} tone="ok" />
          )}
        </div>

        <div className="mt-5">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Line items</div>
          <div className="border border-slate-200 rounded-md divide-y">
            {(q.items || []).map((it, i) => (
              <div key={i} className="p-2.5 flex items-center gap-3 text-[12px]"
                data-testid={`quote-line-${i}`}>
                <div className="flex-1">
                  <div className="font-medium text-slate-900">
                    {(it.line_type || "").toUpperCase() === "SERVICE" &&
                      <span className="text-[8.5px] uppercase tracking-wider px-1 py-0.5 rounded bg-violet-50 text-violet-700 ring-1 ring-violet-200 mr-1.5">SERVICE</span>}
                    {it.product_name}
                  </div>
                  <div className="text-[10px] text-slate-500 font-mono">
                    {(it.line_type || "").toUpperCase() === "SERVICE"
                      ? (it.service_code || "")
                      : customerPartNumber(it, "")} · qty {it.quantity}
                    {it.discount_pct ? ` · disc ${it.discount_pct}%` : ""}
                  </div>
                  {it.description && (
                    <div className="text-[10.5px] text-slate-500 whitespace-pre-wrap mt-0.5 max-w-md"
                        data-testid={`quote-line-desc-${i}`}>{it.description}</div>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-slate-500">Unit</div>
                  <div className="font-mono">{fmtMoney2(it.unit_price_inr, q.currency)}</div>
                </div>
                {costVisible && it.vendor_unit_cost_inr != null && (
                  <div className="text-right">
                    <div className="text-[10px] text-slate-500">Cost</div>
                    <div className="font-mono text-slate-500">{fmtMoney2(it.vendor_unit_cost_inr, q.currency)}</div>
                  </div>
                )}
                <div className="text-right w-24">
                  <div className="text-[10px] text-slate-500">Total</div>
                  <div className="font-mono font-semibold">{fmtMoney2(it.line_total_inr, q.currency)}</div>
                </div>
                {it.product_id && (
                  <button onClick={() => { setTechFilterPid(it.product_id); setShowTechDocs(true); }}
                    data-testid={`quote-line-docs-${i}`}
                    title="Related tech documents"
                    className="text-[10px] px-1.5 py-1 rounded border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 text-slate-600">
                    Docs
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <div>
            <Label>Status</Label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}
              className="w-full h-9 border border-slate-300 rounded-md text-[13px] px-2"
              data-testid="quote-status-select">
              {[...(status === "INTERNAL_REVIEW" ? ["INTERNAL_REVIEW"] : []), "SENT", "ACCEPTED", "REJECTED", "EXPIRED", "CANCELLED"].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-3">
          <Label>Internal notes (hidden from customer)</Label>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
            data-testid="quote-internal-notes" />
        </div>
        <div className="mt-3">
          <Label>Customer notes</Label>
          <Textarea rows={2} value={customerNotes}
            onChange={(e) => setCustomerNotes(e.target.value)}
            data-testid="quote-customer-notes" />
        </div>

        <div className="mt-5 space-y-2"><NextActionBadge kind="quotation" id={q.id} /><QuoteRecordActions q={q} onSaved={onSaved} /></div>
        <div className="mt-5 flex gap-2 flex-wrap">
          <Button variant="outline" onClick={async () => {
              try {
                const res = await api.get(`/vtiger/quotations/${q.id}/pdf/preview`,
                  { responseType: "blob" });
                window.open(URL.createObjectURL(res.data), "_blank");
              } catch { toast.error("Preview failed"); }
            }} data-testid="quote-pdf-preview">
            <FileText className="h-4 w-4 mr-1.5" /> Preview PDF
          </Button>
          <Button variant="outline" disabled={pdfBusy} aria-busy={pdfBusy} onClick={async () => {
              if (pdfBusy) return;
              setPdfBusy(true);
              try {
                const res = await api.get(`/vtiger/quotations/${q.id}/pdf/download`,
                  { responseType: "blob" });
                const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = `Quotation-${q.code || q.id}-v${q.quote_version || 1}.pdf`;
                a.click(); URL.revokeObjectURL(url);
              } catch (e) { toast.error(e.response?.status === 503 ? "PDF generator is busy. Please try again in a moment." : "Download failed"); }
              finally { setPdfBusy(false); }
            }} data-testid="quote-pdf-download">
            {pdfBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileText className="h-4 w-4 mr-1.5" />}
            {pdfBusy ? "Preparing PDF…" : "Download PDF"}
          </Button>
          <Button variant="outline" onClick={async () => {
              try {
                const res = await api.post(`/vtiger/quotations/${q.id}/pdf/regenerate`);
                toast.success(`Regenerated · ${res.data.size_bytes} bytes · v${res.data.quote_version}`);
              } catch (e) { toast.error(e.response?.data?.detail || "Regenerate failed"); }
            }} data-testid="quote-pdf-regenerate">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Regenerate PDF
          </Button>
          <IssueQuoteButton quote={q} onIssued={onSaved} />
          <Button variant="outline" onClick={onClose} className="ml-auto">Cancel</Button>
          <Button onClick={save} disabled={busy}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="quote-save">
            <Send className="h-4 w-4 mr-1.5" />
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
        {q?.quote_version > 1 && (
          <div className="mt-2 text-[10px] text-slate-500 flex items-center gap-2">
            Currently on version <span className="font-mono font-semibold">{q.quote_version}</span>
            {q.last_issued_version && <span>· last issued v{q.last_issued_version}</span>}
          </div>
        )}
        </div>
      </div>
      {acceptOpen && (
        <AcceptanceModal decision={acceptOpen} qid={q.id} code={q.code}
          onClose={() => setAcceptOpen(null)}
          onDone={() => { setAcceptOpen(null); onSaved(); }} />
      )}
      {showTechDocs && (
        <TechDocsDrawer quotation={q}
          productIdFilter={techFilterPid}
          onClose={() => setShowTechDocs(false)}
          onChanged={async () => {
            try {
              const { data } = await api.get(
                `/vtiger/quotations/${q.id}/attachments`);
              setTechCount(data.count || 0);
            } catch { /* silent */ }
          }} />
      )}
    </div>
  );
}


function AcceptanceModal({ decision, qid, code, onClose, onDone }) {
  const [notes, setNotes] = useState("");
  const [loiB64, setLoiB64] = useState(null);
  const [loiName, setLoiName] = useState(null);
  const [busy, setBusy] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3_000_000) { toast.error("LOI PDF must be <= 3 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = String(reader.result || "").split(",")[1] || "";
      setLoiB64(b64); setLoiName(f.name);
    };
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/vtiger/quotations/${qid}/acceptance`, {
        decision, notes: notes || null,
        loi_pdf_b64: loiB64 || null, loi_filename: loiName || null,
      });
      toast.success(`Recorded: ${decision.replace("_", " ")}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record");
    } finally { setBusy(false); }
  };

  const tones = {
    ACCEPTED:  { text: "text-emerald-700", btn: "bg-emerald-700 hover:bg-emerald-800" },
    REJECTED:  { text: "text-rose-700",    btn: "bg-rose-700 hover:bg-rose-800" },
    REVISION_REQUESTED: { text: "text-amber-700", btn: "bg-amber-700 hover:bg-amber-800" },
  };
  const t = tones[decision] || tones.ACCEPTED;
  const heading = decision === "ACCEPTED" ? "Record Customer Acceptance"
                : decision === "REJECTED" ? "Record Customer Rejection"
                : "Record Revision Request";

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="acceptance-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-white rounded-md p-6 shadow-xl">
        <div className={`text-[10px] uppercase tracking-[0.14em] ${t.text}`}>
          {code}
        </div>
        <h3 className="text-xl font-heading font-semibold text-slate-900 mt-1">
          {heading}
        </h3>
        <div className="mt-4 space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
              Notes (optional but recommended)
            </div>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Verbal go-ahead received on call · PO to follow"
              data-testid="acceptance-notes" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
              LOI / Confirmation PDF (optional, ≤ 3 MB)
            </div>
            <label className="border border-dashed border-slate-300 rounded-md p-3 flex items-center gap-2 cursor-pointer hover:bg-slate-50">
              <Upload className="h-4 w-4 text-slate-500" />
              <span className="text-[12px] text-slate-700">
                {loiName ? loiName : "Click to attach PDF"}
              </span>
              <input type="file" accept="application/pdf" onChange={onFile}
                className="hidden" data-testid="acceptance-loi-input" />
            </label>
            {loiB64 && (
              <button onClick={() => { setLoiB64(null); setLoiName(null); }}
                className="mt-1 text-[10px] text-rose-700 underline">Remove</button>
            )}
          </div>
        </div>
        <div className="mt-5 flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1 h-10">Cancel</Button>
          <Button onClick={submit} disabled={busy}
            className={`flex-1 h-10 text-white ${t.btn}`}
            data-testid="acceptance-submit">
            {busy ? "Recording…" : `Confirm ${decision.replace("_", " ")}`}
          </Button>
        </div>
      </div>
    </div>
  );
}


function Label({ children }) {
  return <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{children}</div>;
}

function InfoTile({ label, value, bold, tone }) {
  const tint = tone === "ok" ? "text-emerald-700" : "text-slate-900";
  return (
    <div className="border border-slate-200 rounded-md p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-[13px] mt-0.5 ${tint} ${bold ? "font-semibold font-mono" : ""}`}>
        {value}
      </div>
    </div>
  );
}


function QStat({ label, value, tone }) {
  const tint = tone === "ok"   ? "text-emerald-800 bg-emerald-50 border-emerald-200"
             : tone === "warn" ? "text-amber-800 bg-amber-50 border-amber-200"
             : tone === "err"  ? "text-rose-800 bg-rose-50 border-rose-200"
             : "text-slate-700 bg-slate-50 border-slate-200";
  return (
    <div className={"border rounded px-2.5 py-1.5 " + tint}>
      <div className="text-[9px] uppercase tracking-wider truncate">{label}</div>
      <div className="text-[13px] font-heading font-semibold truncate">{value ?? "—"}</div>
    </div>
  );
}


/* ================================================================
 *  ProductPicker — searchable Product Master dropdown used inside
 *  each quotation line item.  Includes an inline "+ Add new
 *  product" modal that POSTs to /product-master/products and
 *  auto-selects the created row.  Uses only actual master data —
 *  no fabrication.
 * ================================================================ */
// Quick spec facets for the picker — parsed from each product's name +
// description (specs aren't structured in master, so we token-match).
const SPEC_GROUPS = [
  { key: "cpu", label: "CPU",
    tokens: ["i3", "i5", "i7", "i9", "Core 5", "Core 7", "Core 9",
             "Ultra 5", "Ultra 7", "Ultra 9", "Ryzen 5", "Ryzen 7", "Ryzen 9",
             "Xeon", "M1", "M2", "M3", "M4"] },
  { key: "ram", label: "RAM",
    tokens: ["4GB", "8GB", "16GB", "32GB", "64GB"] },
  { key: "storage", label: "Storage",
    tokens: ["128GB", "256GB", "512GB", "1TB", "2TB", "4TB"] },
];

const _normSpec = (s) => String(s || "").replace(/\u2011/g, "-").toLowerCase();

// A RAM token like "32gb" must not also match a storage "512gb" — anchor GB
// tokens on a word boundary so "32gb" doesn't hit inside "1032gb".
const _hasToken = (text, token) => {
  const t = token.toLowerCase();
  if (/gb$/.test(t)) return new RegExp(`(^|[^0-9])${t}\\b`).test(text);
  return text.includes(t);
};

function ProductPicker({ value, onSelect, index = 0 }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [hits, setHits] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addServiceOpen, setAddServiceOpen] = useState(false);
  const [svcCategories, setSvcCategories] = useState([]);
  const [pickerImgs, setPickerImgs] = useState({});
  const [piOpen, setPiOpen] = useState(false);
  const [specSel, setSpecSel] = useState({ cpu: [], ram: [], storage: [] });

  const toggleSpec = (group, token) => setSpecSel((s) => {
    const cur = s[group];
    return { ...s, [group]: cur.includes(token) ? cur.filter((t) => t !== token) : [...cur, token] };
  });
  const clearSpecs = () => setSpecSel({ cpu: [], ram: [], storage: [] });

  useEffect(() => {
    const pids = hits.filter((h) => (h.line_type || "").toUpperCase() !== "SERVICE")
      .map((h) => h.id);
    if (!pids.length) { setPickerImgs({}); return; }
    api.post("/pc/image-map", { product_ids: pids })
      .then(({ data }) => setPickerImgs(data.images || {})).catch(() => {});
  }, [hits]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      try {
        // Unified picker — returns Products AND Services together, each
        // flagged with line_type so the row + auto-population know how
        // to behave.
        const { data } = await api.get(
            `/service-master/search/quote-picker?limit=25${q ? `&q=${encodeURIComponent(q)}` : ""}`);
        setHits(data.items || []);
      } catch { setHits([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);

  useEffect(() => {
    if (addServiceOpen && svcCategories.length === 0) {
      api.get("/service-master/categories").then(({ data }) =>
          setSvcCategories(data.items || []));
    }
  }, [addServiceOpen, svcCategories.length]);

  const label = value?.product_name || customerPartNumber(value, "") || "";

  // Build spec facets from current hits (only show values that exist) and
  // filter the visible list by the active chips (AND across groups, OR within).
  const _hitText = (h) => _normSpec(`${h.name || ""} ${h.description || ""}`);
  const facets = SPEC_GROUPS.map((g) => ({
    ...g,
    present: g.tokens.filter((tok) => hits.some((h) => _hasToken(_hitText(h), tok))),
  })).filter((g) => g.present.length > 0);
  const anySel = specSel.cpu.length || specSel.ram.length || specSel.storage.length;
  const shown = hits.filter((h) => {
    const text = _hitText(h);
    return SPEC_GROUPS.every((g) => {
      const sel = specSel[g.key];
      return !sel.length || sel.some((tok) => _hasToken(text, tok));
    });
  });

  // Adapt a picker hit → the shape callers expect (product_name +
  // product_code + brand + oem_part_number + standard_selling_price)
  // plus a line_type flag and service_id when the row is a SERVICE.
  const adapt = (hit) => {
    if (!hit) return null;
    const isService = (hit.line_type || "").toUpperCase() === "SERVICE";
    return {
      // Product-shape fields the existing composer already handles
      id:                                       hit.id,
      product_name:               hit.name,
      product_code:               hit.code,
      brand:                                 hit.brand || (isService ? "SERVICE" : null),
      oem_part_number:      isService ? null : (hit.part_number || null),
      description:                 hit.description,
      hsn_sac:                              hit.sac_hsn,
      standard_selling_price: hit.unit_price || 0,
      gst_percent:                   hit.gst_percent ?? 18,
      // Extra fields for line-item persistence
      line_type:                       isService ? "SERVICE" : "PRODUCT",
      service_id:                    isService ? hit.id : null,
      service_code:              isService ? hit.code : null,
      unit_of_measure:      hit.unit_of_measure || "UNIT",
    };
  };

  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen(true)}
        data-testid={`line-product-open-${index}`}
        title={label || undefined}
        className="w-full h-8 px-2 rounded-md border border-slate-200 hover:border-emerald-300 bg-white text-left text-[12px] flex items-center gap-1.5">
        <Package className="h-3 w-3 text-emerald-700 shrink-0" />
        <span className="truncate flex-1">
          {label || <span className="text-slate-400">Search product or service…</span>}
        </span>
        <ChevronDown className="h-3 w-3 text-slate-400 shrink-0" />
      </button>

      {open && (
        <div className="absolute z-30 top-full left-0 mt-1 w-[560px] max-w-[85vw] bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden"
          data-testid={`line-product-menu-${index}`}>
          <div className="p-2 border-b border-slate-100">
            <input autoFocus value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search product / service…"
              data-testid={`line-product-search-${index}`}
              className="w-full h-8 px-2 outline-none text-[12px] border border-slate-200 rounded" />
          </div>

          {facets.length > 0 && (
            <div className="px-2 py-2 border-b border-slate-100 bg-slate-50/60 space-y-1.5"
              data-testid={`line-product-filters-${index}`}>
              {facets.map((g) => (
                <div key={g.key} className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 w-12 shrink-0">{g.label}</span>
                  {g.present.map((tok) => {
                    const on = specSel[g.key].includes(tok);
                    return (
                      <button key={tok} type="button"
                        onClick={() => toggleSpec(g.key, tok)}
                        data-testid={`spec-chip-${g.key}-${tok}`}
                        className={"text-[10px] px-2 py-0.5 rounded-full border transition-colors " +
                          (on ? "bg-emerald-600 text-white border-emerald-600"
                              : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300")}>
                        {tok}
                      </button>
                    );
                  })}
                </div>
              ))}
              {anySel > 0 && (
                <button type="button" onClick={clearSpecs}
                  data-testid={`spec-clear-${index}`}
                  className="text-[10px] text-rose-600 hover:underline">Clear filters</button>
              )}
            </div>
          )}

          <div className="max-h-72 overflow-y-auto">
            {shown.length === 0 && (
              <div className="p-3 text-center text-[12px] text-slate-500 italic">
                {hits.length && anySel ? "No products match these filters" : "No products or services found"}
                {!anySel && q.trim().length >= 5 && (
                  <div className="mt-2 not-italic rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-left" data-testid="qp-not-in-master">
                    <div className="font-semibold text-indigo-900 text-[11px]">PRODUCT NOT IN PRODUCT MASTER — <span className="font-mono">{q.trim()}</span></div>
                    <button type="button" onClick={() => { setPiOpen(true); }} className="mt-1 inline-flex items-center gap-1 h-7 px-2.5 rounded bg-indigo-600 text-white text-[11px] font-medium" data-testid="qp-pi-fetch"><Sparkles className="h-3 w-3" /> Fetch Product Intelligence</button>
                    <span className="ml-2 text-[10.5px] text-indigo-800">Exact match → Add to Product Master & Quote, without leaving the quotation.</span>
                  </div>
                )}
              </div>
            )}
            {shown.map((p) => {
              const isSvc = (p.line_type || "").toUpperCase() === "SERVICE";
              return (
                <button key={`${p.line_type}-${p.id}`}
                  data-testid={`line-picker-hit-${p.line_type}-${p.id}`}
                  onClick={() => { onSelect(adapt(p)); setOpen(false); setQ(""); }}
                  className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-[12px] border-b last:border-b-0 border-slate-100">
                  <div className="flex items-start gap-1.5">
                    {!isSvc && pickerImgs[p.id] && (
                      <img src={pickerImgs[p.id]} alt="" loading="lazy"
                        className="h-8 w-8 object-contain rounded border border-slate-100 bg-white shrink-0"
                        data-testid={`picker-thumb-${p.id}`} />)}
                    <span className={"mt-0.5 shrink-0 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ring-1 " +
                        (isSvc
                            ? "bg-violet-50 text-violet-700 ring-violet-200"
                            : "bg-emerald-50 text-emerald-700 ring-emerald-200")}>
                      {p.line_type}
                    </span>
                    <span className="font-medium flex-1 break-words">{p.name}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {(isSvc ? p.code : p.part_number) || "—"} · {p.category || "—"}
                    {p.unit_price != null && <> · {fmtINR(p.unit_price)}</>}
                    {isSvc && p.sac_hsn && <> · SAC {p.sac_hsn}</>}
                    {!isSvc && p.qty_in_stock != null && <> · stock {p.qty_in_stock}</>}
                  </div>
                  {p.description ? (
                    <div className="text-[10px] text-slate-400 line-clamp-2 mt-0.5">{p.description}</div>
                  ) : (
                    <div className="text-[10px] text-rose-700 mt-0.5">Description required in master</div>
                  )}
                </button>
              );
            })}
          </div>
          <div className="border-t border-slate-100 flex">
            <button className="flex-1 px-3 py-2 text-[12px] text-emerald-700 hover:bg-emerald-50 flex items-center gap-1"
              data-testid="line-product-add"
              onClick={() => { setOpen(false); setAddOpen(true); }}>
              <Plus className="h-3 w-3" /> Add product
            </button>
            <button className="flex-1 px-3 py-2 text-[12px] text-violet-700 hover:bg-violet-50 flex items-center gap-1 border-l border-slate-100"
              data-testid="line-service-add"
              onClick={() => { setOpen(false); setAddServiceOpen(true); }}>
              <Plus className="h-3 w-3" /> Add service
            </button>
            <button className="px-3 py-2 text-[12px] text-slate-500 hover:bg-slate-50 border-l border-slate-100"
              onClick={() => setOpen(false)}>
              Close
            </button>
          </div>
        </div>
      )}

      {addOpen && (
        <AddProductModal
          context="Quotation"
          prefill={q.trim() ? { oem_part_number: q.trim() } : null}
          onClose={() => setAddOpen(false)}
          onCreated={(p) => { onSelect(adapt({ ...p,
                              line_type: "PRODUCT",
                              name: p.product_name,
                              code: p.product_code,
                              sac_hsn: p.hsn_sac,
                              unit_price: p.standard_selling_price,
                              gst_percent: p.gst_percent })); setAddOpen(false); }} />
      )}
      {piOpen && (
        <ProductEnrichPanel open={piOpen} onOpenChange={setPiOpen} partNumber={q.trim()} mode="create"
          onApplied={({ product: p }) => { if (!p) return; onSelect(adapt({ ...p, line_type: "PRODUCT", name: p.product_name, code: p.product_code, sac_hsn: p.hsn_sac, unit_price: p.standard_selling_price, gst_percent: p.gst_percent })); setOpen(false); setQ(""); }} />
      )}
      {addServiceOpen && (
        <QuoteAddServiceModal
          categories={svcCategories} presetName={q}
          onClose={() => setAddServiceOpen(false)}
          onCreated={(s) => { onSelect(adapt({
              line_type: "SERVICE",
              id: s.id,
              name: s.service_name,
              code: s.service_code,
              category: s.category,
              description: s.description,
              sac_hsn: s.sac_code,
              gst_percent: s.gst_percent,
              unit_price: s.standard_selling_price,
              unit_of_measure: s.unit_of_measure,
          })); setAddServiceOpen(false); }} />
      )}
    </div>
  );
}

// Lightweight service-create modal used from the quotation composer.
// Persists into the SAME Service Master (no quotation-only ghost row).
function QuoteAddServiceModal({ categories, presetName, onClose, onCreated }) {
  const [form, setForm] = useState({
    service_name: presetName || "", service_code: "", category: "",
    description: "", sac_code: "", gst_percent: 18,
    standard_selling_price: 0, unit_of_measure: "UNIT",
    delivery_mode: "", status: "ACTIVE", source: "MANUAL",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.service_name.trim()) return toast.error("Service name required");
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.delivery_mode) delete payload.delivery_mode;
      const { data } = await api.post("/service-master", payload);
      toast.success(`Service added: ${data.service_name}`);
      onCreated?.(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-40 bg-slate-900/50 grid place-items-center p-4"
        data-testid="line-svc-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-xl">
        <div className="p-3 border-b border-slate-200 flex items-center justify-between">
          <div className="font-heading font-semibold text-slate-900 text-[13px]">Add service</div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-3 grid grid-cols-2 gap-2 text-[11px]">
          <div className="col-span-2">
            <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-0.5">Service name *</div>
            <input value={form.service_name} onChange={(e) => setForm({...form, service_name: e.target.value})}
                data-testid="line-svc-name"
                className="w-full border border-slate-200 rounded-md px-2 py-1.5" />
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-0.5">Service code</div>
            <input value={form.service_code} onChange={(e) => setForm({...form, service_code: e.target.value})}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5 font-mono" />
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-0.5">Category</div>
            <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5">
              <option value="">—</option>
              {categories.map((c) => <option key={c.key || c.id} value={c.label}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-0.5">SAC</div>
            <input value={form.sac_code} onChange={(e) => setForm({...form, sac_code: e.target.value})}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5 font-mono" />
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-0.5">GST %</div>
            <input type="number" value={form.gst_percent} onChange={(e) => setForm({...form, gst_percent: parseFloat(e.target.value) || 0})}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5" />
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-0.5">Rate (INR)</div>
            <input type="number" value={form.standard_selling_price} onChange={(e) => setForm({...form, standard_selling_price: parseFloat(e.target.value) || 0})}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5" />
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-0.5">Unit</div>
            <select value={form.unit_of_measure} onChange={(e) => setForm({...form, unit_of_measure: e.target.value})}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5">
              {["UNIT", "HOUR", "DAY", "MONTH", "PROJECT", "DEVICE", "USER", "SITE", "INCIDENT"].map((u) =>
                <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-0.5">Description *</div>
            <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}
                rows={2} data-testid="line-svc-desc"
                className="w-full border border-slate-200 rounded-md px-2 py-1.5" />
          </div>
        </div>
        <div className="p-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
          <button onClick={submit} disabled={busy}
              data-testid="line-svc-submit"
              className="text-[11px] px-3 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-50">
            {busy ? "Saving…" : "Save & add"}
          </button>
        </div>
      </div>
    </div>
  );
}





/* ================================================================
 *  TechDocsDrawer — right-side drawer inside the Quotation view.
 *  Lists CUSTOMER_SAFE tech docs for products on the quote, with
 *  attach checkboxes.  Also supports uploading a new tech doc via
 *  the existing Tech Library (pre-selecting the current product).
 * ================================================================ */

function TechDocsDrawer({ quotation, productIdFilter, onClose, onChanged }) {
  const [candidates, setCandidates] = useState([]);
  const [attached, setAttached] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploadFor, setUploadFor] = useState(null);
  const [preview, setPreview] = useState(null);
  const [pids, setPids] = useState([]);
  const [pidFilter, setPidFilter] = useState(productIdFilter || "");
  const [packSummary, setPackSummary] = useState(null);

  const loadSummary = useCallback(() => {
    api.get(`/vtiger/quotations/${quotation.id}/pack/summary`)
      .then(({ data }) => setPackSummary(data)).catch(() => setPackSummary(null));
  }, [quotation.id]);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [candResp, attResp] = await Promise.all([
        api.get(`/vtiger/quotations/${quotation.id}/tech-documents${pidFilter ? `?product_id=${pidFilter}` : ""}`),
        api.get(`/vtiger/quotations/${quotation.id}/attachments`),
      ]);
      setCandidates(candResp.data.items || []);
      setPids(candResp.data.product_ids || []);
      setAttached(attResp.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load tech docs");
    } finally { setBusy(false); }
  }, [quotation.id, pidFilter]);
  useEffect(() => { load(); loadSummary(); }, [load, loadSummary]);

  const toggle = async (doc) => {
    // Attach or detach
    if (doc.is_attached) {
      const att = attached.find((a) => a.document_id === doc.id);
      if (!att) return;
      if (att.snapshot_at_issue) {
        toast.error("Attachment locked at issue — clone the quote to change.");
        return;
      }
      try {
        await api.delete(`/vtiger/quotations/${quotation.id}/attachments/${att.id}`);
        toast.success("Detached");
        load(); loadSummary(); onChanged();
      } catch (e) { toast.error(e.response?.data?.detail || "Detach failed"); }
    } else {
      try {
        await api.post(`/vtiger/quotations/${quotation.id}/attachments`,
          { document_ids: [doc.id] });
        toast.success("Attached");
        load(); loadSummary(); onChanged();
      } catch (e) { toast.error(e.response?.data?.detail || "Attach failed"); }
    }
  };

  const productOnQuote = (quotation.items || [])
    .filter((it) => it.product_id)
    .find((it) => it.product_id === pidFilter);
  const preselect = productOnQuote
    ? { id: productOnQuote.product_id,
          product_name: productOnQuote.product_name,
          product_code: productOnQuote.product_code,
          brand:                productOnQuote.brand }
    : null;

  return (
    <div className="fixed inset-0 z-[70] bg-black/30" onClick={onClose}
      data-testid="quote-tech-docs-drawer">
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-xl bg-white shadow-xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">
              Tech documents
            </div>
            <h3 className="text-lg font-heading font-semibold">
              For quote {quotation.code}
            </h3>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Only <span className="font-semibold">Customer safe</span> documents
              can be attached.
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 text-[13px]"
            data-testid="quote-tech-docs-close">Close ✕</button>
        </div>

        {/* Product filter chips */}
        <div className="p-3 flex flex-wrap gap-1.5 border-b border-slate-100">
          <button
            onClick={() => setPidFilter("")}
            className={"text-[11px] px-2 py-0.5 rounded border " +
              (pidFilter === ""
                ? "bg-emerald-700 text-white border-emerald-700"
                : "border-slate-200 hover:border-emerald-300")}>
            All products
          </button>
          {(quotation.items || []).filter((it) => it.product_id).map((it) => (
            <button key={it.product_id}
              data-testid={`quote-tech-filter-${it.product_id}`}
              onClick={() => setPidFilter(it.product_id)}
              className={"text-[11px] px-2 py-0.5 rounded border truncate max-w-[220px] " +
                (pidFilter === it.product_id
                  ? "bg-emerald-700 text-white border-emerald-700"
                  : "border-slate-200 hover:border-emerald-300")}>
              {it.product_name}
            </button>
          ))}
        </div>

        {/* Candidate documents */}
        <div className="p-3">
          {busy && candidates.length === 0 && (
            <div className="text-[12px] text-slate-500">Loading…</div>
          )}
          {!busy && candidates.length === 0 && (
            <div className="text-[12px] text-slate-500 italic p-3 text-center border border-dashed rounded">
              No Customer-safe documents found for the products on this quote.
              {" "}Upload one below to make it available.
            </div>
          )}
          <ul className="divide-y divide-slate-100">
            {candidates.map((d) => (
              <li key={d.id}
                data-testid={`quote-tech-cand-${d.id}`}
                className="py-2 flex items-center gap-3">
                <input type="checkbox"
                  checked={!!d.is_attached}
                  onChange={() => toggle(d)}
                  data-testid={`quote-tech-toggle-${d.id}`}
                  className="h-4 w-4 accent-emerald-700" />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-slate-900 truncate">
                    {d.title}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {d.product_name || d.brand || ""} · v{d.version} · {d.filename}
                  </div>
                </div>
                <button onClick={() => setPreview(d)}
                  data-testid={`quote-tech-preview-${d.id}`}
                  className="text-[10px] px-1.5 py-1 rounded border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 text-slate-600">
                  Preview
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Already-attached (incl. locked) */}
        {attached.length > 0 && (
          <div className="p-3 border-t border-slate-100">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
              Currently attached ({attached.length})
            </div>
            <ul className="text-[12px] space-y-1">
              {attached.map((a) => (
                <li key={a.id} className="flex items-center gap-2"
                  data-testid={`quote-tech-att-${a.id}`}>
                  <span className="flex-1 truncate">
                    {a.title} · v{a.version}
                  </span>
                  {a.snapshot_at_issue && (
                    <span className="text-[9px] uppercase tracking-wider text-amber-800 bg-amber-50 border border-amber-200 rounded px-1">
                      locked at issue
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Upload new + Download pack */}
        <div className="p-3 border-t border-slate-100 flex flex-wrap gap-2 items-center">
          <Button size="sm" variant="outline"
            data-testid="quote-tech-add"
            onClick={() => setUploadFor(preselect)}>
            <Plus className="h-3 w-3 mr-1" /> Add tech document
          </Button>
          <a href={`${api.defaults?.baseURL || ""}/vtiger/quotations/${quotation.id}/pack/preview`}
            target="_blank" rel="noreferrer"
            data-testid="quote-tech-preview-pack"
            className="text-[12px] px-3 py-1.5 rounded border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 text-slate-700 flex items-center gap-1">
            <FileText className="h-3 w-3" /> Preview pack
          </a>
          <a href={`${api.defaults?.baseURL || ""}/vtiger/quotations/${quotation.id}/pack`}
            data-testid="quote-tech-download-pack"
            className="text-[12px] px-3 py-1.5 rounded border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-medium flex items-center gap-1">
            <FileText className="h-3 w-3" /> Download complete quote pack
          </a>
          <a href={`${api.defaults?.baseURL || ""}/vtiger/quotations/${quotation.id}/pack?format=zip`}
            data-testid="quote-tech-download-zip"
            className="text-[11px] text-slate-500 hover:text-slate-700 underline">
            separate files (.zip)
          </a>
          {packSummary && (
            <div className="w-full text-[11px] text-slate-500 mt-1" data-testid="quote-pack-summary">
              Quote {packSummary.quote_pages}p
              {packSummary.included.map((i) => ` · ${i.title} ${i.pages}p`).join("")}
              {" · "}<span className="font-semibold text-slate-700">Total {packSummary.total_pages}p</span>
              {packSummary.missing.length > 0 && (
                <span className="text-amber-700"> · {packSummary.missing.length} not merged (non-PDF)</span>
              )}
            </div>
          )}
        </div>
      </div>

      {uploadFor !== null && (
        <TechUploadModal preselectProduct={uploadFor}
          onClose={() => setUploadFor(null)}
          onSaved={() => { setUploadFor(null); load(); }} />
      )}
      {preview && (
        <TechPreviewModal doc={preview} onClose={() => setPreview(null)} />
      )}
    </div>
  );
}

/* ============================================================
 *  Compact inline preview for a Tech Library document.  Uses
 *  iframe/img fallback identical to the main library.
 * ============================================================ */
function TechPreviewModal({ doc, onClose }) {
  const src = `${api.defaults?.baseURL || ""}/tech-library/documents/${doc.id}/file`;
  const isPdf = (doc.file_type || "").includes("pdf");
  const isImg = (doc.file_type || "").startsWith("image/");
  return (
    <div className="fixed inset-0 z-[80] bg-black/50 grid place-items-center p-4"
      onClick={onClose} data-testid="quote-tech-preview">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        <div className="p-3 border-b border-slate-100 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">
              {doc.doc_type}
            </div>
            <h3 className="text-base font-heading font-semibold truncate">
              {doc.title}
            </h3>
          </div>
          <button onClick={onClose} className="text-slate-500 text-[13px]">Close ✕</button>
        </div>
        <div className="flex-1 overflow-auto p-3">
          {isPdf ? (
            <iframe src={src} title={doc.title}
              className="w-full h-[70vh] border border-slate-200 rounded" />
          ) : isImg ? (
            <img src={src} alt={doc.title}
              className="max-w-full max-h-[70vh] border border-slate-200 rounded" />
          ) : (
            <div className="p-8 text-center text-[12px] text-slate-500">
              Inline preview isn’t available for this file type.
              <a href={src} download className="ml-2 text-emerald-700 underline">
                Download to view
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


/* ================================================================
 *  SezTaxChip — compact banner shown at the top of the customer
 *  summary inside NewQuoteDrawer.  Reads the SEZ profile stored on
 *  the customer master and shows verified vs review states.
 * ================================================================ */
function SezTaxChip({ customer }) {
  const profile = customer.sez_profile || {};
  const treat = customer.gst_treatment || profile.gst_treatment;
  const verified = profile.verification_status === "VERIFIED"
                                && profile.authorised_operations;
  const tone = !treat || treat === "REGULAR"
    ? "text-slate-700 bg-slate-50 border-slate-200"
    : verified
      ? "text-emerald-800 bg-emerald-50 border-emerald-200"
      : "text-amber-800 bg-amber-50 border-amber-200";
  const label = treat === "SEZWOP" ? "SEZ — Zero rated (LUT)"
                       : treat === "SEZWP"  ? "SEZ — With IGST"
                       : treat === "EXPORT" ? "Export"
                       : "Regular GST";
  return (
    <div className={"mb-2 px-2 py-1.5 rounded border text-[11px] flex items-center gap-2 " + tone}
      data-testid="new-quote-sez-chip">
      <span className="font-semibold uppercase tracking-wider text-[10px]">
        {label}
      </span>
      {treat !== "REGULAR" && !verified && (
        <span className="italic">Not verified — tax will fall back to REVIEW REQUIRED</span>
      )}
      {profile.sez_unit_name && (
        <span className="ml-auto font-mono">
          {profile.sez_unit_name} · {profile.sez_gstin || "no GSTIN"}
        </span>
      )}
    </div>
  );
}

